import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Settings,
  Videocam,
  Memory,
  Speed,
  Timer,
  CheckCircle,
  Error as ErrorIcon,
  Timeline,
  Save,
  Storage,
  NetworkCheck,
  FiberManualRecord,
  Terminal,
  CameraAlt,
  Computer,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Person,
  Security,
  InfoOutlined,
  DirectionsRun,
  ExpandMore,
  ExpandLess,
  Link as LinkIcon,
  DeviceThermostat,
  Add,
  Delete,
  Sync,
} from '@mui/icons-material';
import { devicesApi, Device } from '../services/devicesApi';
import { api } from '../services/api';
import { getDeviceLifecycleMetadata } from '../utils/statusMetadata';
import { getStoredAccessToken } from '../services/authStorage';
import { FirmwareReadinessPanel } from '../components/FirmwareReadinessPanel';
import { EnterpriseProductionDeployPanel } from '../components/enterpriseDeploy/EnterpriseProductionDeployPanel';
import SuggestedActionsRail from '../components/SuggestedActionsRail';
import { PlaybookCallout, buildDocsUrl } from '../components/PlaybookCallout';
import {
  incidentCanonicalEvidenceReadinessLabel,
  incidentCanonicalSourceContextLabel,
} from '../components/incidents/reviewCanonical';

// Types
interface PipelineStatus {
  device_id: string;
  pipeline_name: string;
  state: 'running' | 'stopped' | 'starting' | 'stopping' | 'switching' | 'error' | 'unknown' | 'online';
  uptime_seconds: number;
  last_switch: string | null;
  available_pipelines: string[];
  capabilities: Record<string, boolean>;
  error: string | null;
  timestamp: string;
  message?: string;
  restart_required?: boolean;
  rtsp_url?: string;
  ip_address?: string;
  mac_address?: string;
  last_seen?: string;
}

type CameraDisplayMode = 'auto' | 'wayland' | 'headless';

interface DeviceMetrics {
  fps: number;
  latency_ms: number;
  cpu_percent: number;
  memory_percent: number;
  gpu_percent?: number;
  temperature_c?: number | null;
  thermal_status?: string | null;
  detections_per_second: number;
  alerts_total: number;
  uptime_hours: number;
}

interface RegistryCamera {
  camera_id: string;
  device_id: string;
  display_name?: string | null;
  slug?: string | null;
  source?: any;
  health?: any;
  created_at?: string;
  updated_at?: string;
}

interface RegistryPolicy {
  policy_id: string;
  device_id: string;
  camera_id?: string | null;
  policy_type: string;
  display_name?: string |null;
  enabled?: boolean;
  config?: any;
}

interface RegistryPipelinesResponse {
  ok?: boolean;
  cameras?: Array<{
    camera_id: string;
    alive: boolean;
    pid?: number | null;
    pipeline?: string;
    source_type?: string;
    rtsp_url?: string | null;
    runtime_desired_state?: string | null;
    runtime_override?: any;
    source_reachable?: boolean | null;
    source_error?: string | null;
    meta_path?: string | null;
    log_path?: string | null;
    log_tail?: string[] | null;
    log_tail_truncated?: boolean | null;
    last_error_line?: string | null;
  }>;
  running_count?: number;
}

interface IncidentE2EHealth {
  ok?: boolean;
  timestamp?: string;
  checks?: any;
  error?: string;
}

interface DeviceControlSummary {
  ok?: boolean;
  device?: Device;
  runtime?: {
    pipeline_status?: PipelineStatus | null;
    metrics?: Partial<DeviceMetrics> | null;
  };
  registry_summary?: {
    camera_count?: number;
    camera_health_rollup?: {
      ok?: number;
      warning?: number;
      error?: number;
    };
    pipeline_rollup?: {
      running?: number;
      stopped?: number;
    };
    policy_rollup?: {
      device_wide_count?: number;
      camera_scoped_count?: number;
      cameras_with_no_policies?: number;
    };
  };
  health_rollup?: {
    incident_e2e_ok?: boolean | null;
    release_agent_status?: string | null;
    has_active_runner_failure?: boolean;
  };
  latest_incident?: {
    incident_id?: string | null;
    created_at?: string | null;
    canonical_labels?: {
      incident_label?: string | null;
    };
    evidence_readiness_state?: string | null;
    proof_scope?: {
      proof_scope_id?: string | null;
    };
    safety_case?: {
      labels?: {
        incident_label?: string | null;
      };
      evidence?: {
        readiness_state?: string | null;
      };
      proof_scope?: {
        proof_scope_id?: string | null;
      };
      traceability?: {
        proof_scope_id?: string | null;
      };
    };
  } | null;
}

export function deviceControlIncidentLabel(incident: DeviceControlSummary['latest_incident']): string {
  const label = String(
    incident?.canonical_labels?.incident_label
      || incident?.safety_case?.labels?.incident_label
      || ''
  ).trim();
  return label || 'Incident';
}

export function deviceControlIncidentEvidenceState(incident: DeviceControlSummary['latest_incident']): string {
  const canonical = incidentCanonicalEvidenceReadinessLabel(incident);
  if (canonical) return canonical;
  return String(incident?.evidence_readiness_state || '').trim().toUpperCase();
}

export function deviceControlIncidentSourceContext(incident: DeviceControlSummary['latest_incident']): string {
  return incidentCanonicalSourceContextLabel(incident);
}

export function deviceControlIncidentProofScopeId(incident: DeviceControlSummary['latest_incident']): string {
  return String(
    incident?.proof_scope?.proof_scope_id
      || incident?.safety_case?.traceability?.proof_scope_id
      || incident?.safety_case?.proof_scope?.proof_scope_id
      || ''
  ).trim();
}

interface AuditLogItem {
  id: string;
  timestamp: string;
  action: string;
  resource_type: string;
  resource_id: string;
  username?: string | null;
  success?: boolean;
  error_message?: string | null;
  details?: any;
}

type StopTtlOption = { label: string; ttlSec: number | null; helper?: string };

function _basename(p: unknown): string {
  const s = String(p ?? '').trim();
  if (!s) return '';
  const parts = s.split('/');
  return parts[parts.length - 1] || s;
}

function normalizeModelsInUse(
  models: unknown
): Array<{ role: string; modelPath: string; labelsPath?: string }> {
  if (Array.isArray(models)) {
    return models
      .map((m: any) => ({
        role: String(m?.role || ''),
        modelPath: String(m?.model_path || m?.modelPath || ''),
        labelsPath: m?.labels_path ? String(m.labels_path) : m?.labelsPath ? String(m.labelsPath) : undefined,
      }))
      .filter((m) => m.role && m.modelPath);
  }
  return [];
}

function parseModelsFromPipelineLogTail(lines: unknown): Array<{ role: string; modelPath: string; labelsPath?: string }> {
  if (!Array.isArray(lines)) return [];
  const out: Array<{ role: string; modelPath: string; labelsPath?: string }> = [];
  for (const raw of lines) {
    const s = String(raw ?? '');
    const m = s.match(/\[pipeline\]\s+([A-Za-z0-9_]+)\s+model:\s*([^;]+)(?:;\s*labels:\s*([^;]+))?/);
    if (!m) continue;
    const roleRaw = String(m[1] ?? '').trim().toLowerCase();
    const role =
      roleRaw === 'det'
        ? 'detection'
        : roleRaw === 'pose'
        ? 'pose'
        : roleRaw === 'ppe'
        ? 'ppe'
        : roleRaw === 'reid'
        ? 'reid'
        : roleRaw;
    const modelPath = String(m[2] ?? '').trim();
    const labelsPath = String(m[3] ?? '').trim() || undefined;
    if (!modelPath) continue;
    out.push({ role, modelPath, labelsPath });
  }
  const byRole: Record<string, { role: string; modelPath: string; labelsPath?: string }> = {};
  for (const r of out) byRole[r.role] = r;
  return Object.values(byRole);
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`device-tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const isDocumentVisible = (): boolean => (
  typeof document === 'undefined' || document.visibilityState === 'visible'
);

const LAST_DEVICE_CONTROL_ROUTE_KEY = 'ui.last_device_control_route_id';

function looksLikeDeviceRouteUuid(value: string): boolean {
  const v = String(value || '').trim();
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(v);
}

const FeatureChip: React.FC<{ label: string; enabled: boolean; color: 'primary' | 'info' | 'warning' }> = ({
  label,
  enabled,
  color,
}) => (
  <Chip
    size="small"
    label={label}
    color={enabled ? color : 'default'}
    variant={enabled ? 'filled' : 'outlined'}
    sx={{ fontWeight: enabled ? 600 : 400 }}
  />
);

const DeviceControl: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();

  // State
  const [device, setDevice] = useState<Device | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const initialLoadRef = useRef(true);
  const pipelineStatusInFlightRef = useRef(false);
  const metricsInFlightRef = useRef(false);
  const [bootstrapSummary, setBootstrapSummary] = useState<DeviceControlSummary | null>(null);
  const defaultTabDetailsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const auditTailTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [controlDeviceOptions, setControlDeviceOptions] = useState<Device[]>([]);
  const [_controlDeviceOptionsLoading, setControlDeviceOptionsLoading] = useState(false);
  const [showModelsPanel, setShowModelsPanel] = useState(false);
  const [softwareBundleExpanded, setSoftwareBundleExpanded] = useState(false);
  const [softwareOtaExpanded, setSoftwareOtaExpanded] = useState(false);

  // Control Center (recent audit + preflight + diagnostic bundle)
  const [auditTail, setAuditTail] = useState<AuditLogItem[]>([]);
  const [auditTailLoading, setAuditTailLoading] = useState(false);
  const [edgePreflightRunning, setEdgePreflightRunning] = useState(false);
  const [edgePreflightResult, setEdgePreflightResult] = useState<any | null>(null);
  const [diagJobId, setDiagJobId] = useState<string | null>(null);
  const [diagJobStatus, setDiagJobStatus] = useState<any | null>(null);
  const diagPollRef = useRef<any>(null);

  // Pipeline/AI state
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  // IMPORTANT: /devices/:deviceId/control uses the DB UUID as the route param.
  // Most pipeline/edge proxy endpoints expect the *device_id* (string like "warehouse-1_...").
  const pipelineDeviceId = device?.device_id || null;
  const grafanaBase = (process.env.REACT_APP_GRAFANA_URL || '').trim().replace(/\/+$/, '');
  const grafanaPilotOverviewUrl =
    grafanaBase && pipelineDeviceId
      ? `${grafanaBase}/d/pilot-overview?var-device_id=${encodeURIComponent(pipelineDeviceId)}&from=now-6h&to=now`
      : null;

  // Metrics state
  const [metricsLoaded, setMetricsLoaded] = useState(false);
  const [metrics, setMetrics] = useState<DeviceMetrics>({
    fps: 0,
    latency_ms: 0,
    cpu_percent: 0,
    memory_percent: 0,
    temperature_c: null,
    thermal_status: null,
    detections_per_second: 0,
    alerts_total: 0,
    uptime_hours: 0,
  });

  // Connection settings state
  const [connectionSettings, setConnectionSettings] = useState({
    ip_address: '',
    health_api_port: 8080,
  });
  const [connectionSettingsChanged, setConnectionSettingsChanged] = useState(false);
  const [showConnectionSettings, setShowConnectionSettings] = useState(false);
  const [reviewRingSettings, setReviewRingSettings] = useState({
    enabled: true,
    camera_ids: '',
  });
  const [reviewRingSettingsChanged, setReviewRingSettingsChanged] = useState(false);
  const [showReviewRingSettings, setShowReviewRingSettings] = useState(false);

  // Registry cameras (Phase 1)
  const [registryCameras, setRegistryCameras] = useState<RegistryCamera[]>([]);
  const [registryPipelines, setRegistryPipelines] = useState<
    Record<
      string,
      {
        pipeline?: string;
        alive?: boolean;
        runtime_desired_state?: string | null;
        runtime_override?: any;
        source_reachable?: boolean | null;
        source_error?: string | null;
      }
    >
  >({});
  const [edgePipelinesRuntime, setEdgePipelinesRuntime] = useState<
    Record<
      string,
      {
        pipeline?: string;
        alive?: boolean;
        log_tail?: string[] | null;
        models_in_use?: any[] | null;
      }
    >
  >({});
  const [registryPoliciesByCamera, setRegistryPoliciesByCamera] = useState<Record<string, RegistryPolicy[]>>({});
  const [registryDevicePolicies, setRegistryDevicePolicies] = useState<RegistryPolicy[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const registryLoadedRef = useRef(false);

  // Runtime per-camera ops (orchestrator-aware start/stop)
  const [cameraOpLoading, setCameraOpLoading] = useState<Record<string, boolean>>({});
  const [showCrashDetails, setShowCrashDetails] = useState<Record<string, boolean>>({});

  // Stop dialog (prevent accidental indefinite stop)
  const [stopDialog, setStopDialog] = useState<{
    open: boolean;
    cameraId: string;
    ttlSec: number | null;
  }>({ open: false, cameraId: '', ttlSec: 3600 });

  // Logs state
  const [logs, setLogs] = useState<{ source: string; message: string; timestamp: string }[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsEnabled, setLogsEnabled] = useState(false);
  const logsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Incident/Evidence integrated health (proxied via cloud API)
  const [incidentHealth, setIncidentHealth] = useState<IncidentE2EHealth | null>(null);
  const [incidentHealthLoading, setIncidentHealthLoading] = useState(false);
  const [runnerLatest, setRunnerLatest] = useState<any | null>(null);
  const [runnerLoading, setRunnerLoading] = useState(false);
  const [runnerDialogOpen, setRunnerDialogOpen] = useState(false);
  const [runnerDialogRunId, setRunnerDialogRunId] = useState('');
  const [runnerLogs, setRunnerLogs] = useState<any[]>([]);
  const [runnerUseLocalRepo, setRunnerUseLocalRepo] = useState(false);
  const [runnerRepoDir, setRunnerRepoDir] = useState('');
  const [releaseStatus, setReleaseStatus] = useState<any | null>(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseManifestText, setReleaseManifestText] = useState<string>('{}');
  const [releaseDryRun, setReleaseDryRun] = useState(false);
  const [releaseBundlePath, setReleaseBundlePath] = useState<string>('');
  const [releaseAttempts, setReleaseAttempts] = useState<any[]>([]);
  const [releaseAttemptsLoading, setReleaseAttemptsLoading] = useState(false);
  const [releaseAttemptsOpen, setReleaseAttemptsOpen] = useState(false);
  // OTA desired release (cloud pointer) + agent status (edge)
  const [releasesCatalog, setReleasesCatalog] = useState<any[]>([]);
  const [releasesCatalogLoading, setReleasesCatalogLoading] = useState(false);
  const [desiredReleaseInfo, setDesiredReleaseInfo] = useState<any | null>(null);
  const [desiredReleaseLoading, setDesiredReleaseLoading] = useState(false);
  const [desiredReleaseId, setDesiredReleaseId] = useState<string>('');
  const [desiredReleaseDirty, setDesiredReleaseDirty] = useState(false);
  // Models (Cloud Model Registry + edge applied proof)
  const [desiredModels, setDesiredModels] = useState<any | null>(null);
  const [desiredModelsLoading, setDesiredModelsLoading] = useState(false);
  const [desiredModelsError, setDesiredModelsError] = useState<string | null>(null);
  const [desiredStateProof, setDesiredStateProof] = useState<any | null>(null);
  const [desiredStateProofLoading, setDesiredStateProofLoading] = useState(false);
  const [modelsCatalog, setModelsCatalog] = useState<any[]>([]);
  const [modelsCatalogLoading, setModelsCatalogLoading] = useState(false);
  const [setDesiredModelsDialogOpen, setSetDesiredModelsDialogOpen] = useState(false);
  const [setDesiredModelsSaving, setSetDesiredModelsSaving] = useState(false);
  const [selectedModelsForDesired, setSelectedModelsForDesired] = useState<Array<{ model_id: string; name: string; required: boolean; role?: string }>>([]);
  const [otaPrecheckLoading, setOtaPrecheckLoading] = useState(false);
  const [otaPrecheckResult, setOtaPrecheckResult] = useState<any | null>(null);
  const [agentStatus, setAgentStatus] = useState<any | null>(null);
  const [agentStatusLoading, setAgentStatusLoading] = useState(false);
  const [, setDevOtaBuildEnabled] = useState(false);
  const [, setDevOtaBuildJob] = useState<any | null>(null);
  const [, setDevOtaBuildLogs] = useState<string>('');
  const [_devOtaBuildLoading, setDevOtaBuildLoading] = useState(false);
  const devOtaBuildPollRef = useRef<any>(null);
  const [otaVerifyLoading, setOtaVerifyLoading] = useState(false);
  const [otaPolling, setOtaPolling] = useState(false);
  const otaPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const otaPollDeadlineRef = useRef<number | null>(null);

  // Routing test state
  // (moved to Setup Wizard)

  // Deploy / Sync state
  // (moved to Setup Wizard)

  useEffect(() => {
    initialLoadRef.current = true;
    setError(null);
    setBootstrapSummary(null);
    setDevice(null);
    setPipelineStatus(null);
    setMetricsLoaded(false);
    setRegistryLoading(false);
    setRegistryCameras([]);
    setRegistryPipelines({});
    setRegistryPoliciesByCamera({});
    setRegistryDevicePolicies([]);
    setAuditTail([]);
    setEdgePreflightResult(null);
    setEdgePreflightRunning(false);
    setDiagJobId(null);
    setDiagJobStatus(null);
    setIncidentHealth(null);
    setReleaseStatus(null);
    setReleaseDialogOpen(false);
    setReleasesCatalog([]);
    setDesiredReleaseInfo(null);
    setDesiredReleaseId('');
    setDesiredReleaseDirty(false);
    setDesiredModels(null);
    setDesiredModelsError(null);
    pipelineStatusInFlightRef.current = false;
    metricsInFlightRef.current = false;
    setDesiredStateProof(null);
    setModelsCatalog([]);
    setAgentStatus(null);
    setRunnerLatest(null);
    setRunnerDialogOpen(false);
    setRunnerDialogRunId('');
    setRunnerLogs([]);
    setReleaseAttempts([]);
    setReleaseAttemptsOpen(false);
    setShowModelsPanel(false);
    setSoftwareBundleExpanded(false);
    setSoftwareOtaExpanded(false);
    registryLoadedRef.current = false;
    if (defaultTabDetailsTimerRef.current) {
      clearTimeout(defaultTabDetailsTimerRef.current);
      defaultTabDetailsTimerRef.current = null;
    }
    if (auditTailTimerRef.current) {
      clearTimeout(auditTailTimerRef.current);
      auditTailTimerRef.current = null;
    }
  }, [deviceId]);

  const controlDeviceOptionsLoadRef = useRef(false);

  const loadControlDeviceOptions = useCallback(async () => {
    if (controlDeviceOptionsLoadRef.current) return;
    controlDeviceOptionsLoadRef.current = true;
    setControlDeviceOptionsLoading(true);
    try {
      const items = await devicesApi.listDevices();
      const next = (items || [])
        .filter((item) => item?.id && item?.device_id)
        .sort((a, b) => {
          const aLabel = String(a.name || a.device_id || '').toLowerCase();
          const bLabel = String(b.name || b.device_id || '').toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
      setControlDeviceOptions(next);
    } catch (err) {
      console.debug('Could not load device options for DeviceControl selector:', err);
    } finally {
      setControlDeviceOptionsLoading(false);
      controlDeviceOptionsLoadRef.current = false;
    }
  }, []);

  useEffect(() => {
    const currentDeviceId = String(device?.device_id || '').trim();
    if (!currentDeviceId) return;
    try {
      localStorage.setItem('ui.last_device_id', currentDeviceId);
    } catch {
      // Best-effort only.
    }
  }, [device?.device_id]);

  useEffect(() => {
    const id = String(device?.id || '').trim();
    if (!id || !looksLikeDeviceRouteUuid(id)) return;
    try {
      localStorage.setItem(LAST_DEVICE_CONTROL_ROUTE_KEY, id);
    } catch {
      // Best-effort only.
    }
  }, [device?.id]);

  useEffect(() => {
    if (!device?.device_id || window.location.hash !== '#enterprise-production-deploy') return;
    const el = document.getElementById('enterprise-production-deploy');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [device?.device_id]);

  // Fetch device details
  const fetchDevice = useCallback(async (opts?: { manageInitialLoad?: boolean }) => {
    if (!deviceId) return;
    const manageInitialLoad = opts?.manageInitialLoad !== false;

    try {
      // Avoid page-wide flicker by only using the global loading spinner on the very first load.
      if (manageInitialLoad && initialLoadRef.current) setLoading(true);
      const deviceData = await devicesApi.getDevice(deviceId);
      setDevice((prev) => {
        // Prevent re-renders if the fetched device payload is effectively unchanged.
        if (
          prev &&
          prev.id === deviceData.id &&
          prev.device_id === deviceData.device_id &&
          prev.status === deviceData.status &&
          prev.last_seen === deviceData.last_seen &&
          prev.ip_address === deviceData.ip_address &&
          prev.mac_address === deviceData.mac_address &&
          prev.current_config_version === deviceData.current_config_version
        )
          return prev;
        return deviceData;
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load device');
    } finally {
      if (manageInitialLoad && initialLoadRef.current) {
        setLoading(false);
        initialLoadRef.current = false;
      }
    }
  }, [deviceId]);

  const applyBootstrapSummary = useCallback((summary: DeviceControlSummary) => {
    setBootstrapSummary(summary);

    const summaryDevice = summary?.device;
    if (summaryDevice) {
      setDevice((prev) => {
        if (
          prev &&
          prev.id === summaryDevice.id &&
          prev.device_id === summaryDevice.device_id &&
          prev.status === summaryDevice.status &&
          prev.last_seen === summaryDevice.last_seen &&
          prev.ip_address === summaryDevice.ip_address &&
          prev.mac_address === summaryDevice.mac_address &&
          prev.current_config_version === summaryDevice.current_config_version
        ) {
          return prev;
        }
        return summaryDevice;
      });
    }

    const nextPipeline = summary?.runtime?.pipeline_status;
    if (nextPipeline) {
      setPipelineStatus((prev) => {
        if (
          prev &&
          prev.state === nextPipeline.state &&
          prev.pipeline_name === nextPipeline.pipeline_name &&
          prev.uptime_seconds === nextPipeline.uptime_seconds &&
          prev.timestamp === nextPipeline.timestamp
        ) {
          return prev;
        }
        return nextPipeline;
      });
    }

    const nextMetrics = summary?.runtime?.metrics;
    if (nextMetrics) {
      setMetrics((prev) => {
        const next = {
          fps: typeof nextMetrics.fps === 'number' ? nextMetrics.fps : prev.fps,
          latency_ms: typeof nextMetrics.latency_ms === 'number' ? nextMetrics.latency_ms : prev.latency_ms,
          cpu_percent: typeof nextMetrics.cpu_percent === 'number' ? nextMetrics.cpu_percent : prev.cpu_percent,
          memory_percent: typeof nextMetrics.memory_percent === 'number' ? nextMetrics.memory_percent : prev.memory_percent,
          gpu_percent: prev.gpu_percent,
          temperature_c:
            typeof nextMetrics.temperature_c === 'number'
              ? nextMetrics.temperature_c
              : nextMetrics.temperature_c === null
                ? null
                : prev.temperature_c,
          thermal_status:
            typeof nextMetrics.thermal_status === 'string'
              ? nextMetrics.thermal_status
              : prev.thermal_status,
          detections_per_second:
            typeof nextMetrics.detections_per_second === 'number'
              ? nextMetrics.detections_per_second
              : prev.detections_per_second,
          alerts_total:
            typeof nextMetrics.alerts_total === 'number'
              ? nextMetrics.alerts_total
              : prev.alerts_total,
          uptime_hours:
            typeof nextMetrics.uptime_hours === 'number'
              ? nextMetrics.uptime_hours
              : prev.uptime_hours,
        };
        if (
          prev.fps === next.fps &&
          prev.latency_ms === next.latency_ms &&
          prev.cpu_percent === next.cpu_percent &&
          prev.memory_percent === next.memory_percent &&
          prev.temperature_c === next.temperature_c &&
          prev.thermal_status === next.thermal_status &&
          prev.detections_per_second === next.detections_per_second &&
          prev.alerts_total === next.alerts_total &&
          prev.uptime_hours === next.uptime_hours
        ) {
          return prev;
        }
        return next;
      });
      setMetricsLoaded(true);
    }
  }, []);

  const fetchBootstrapSummary = useCallback(async () => {
    if (!deviceId) return;

    if (initialLoadRef.current) setLoading(true);
    // Fast first paint: load the device row from the cloud devices API immediately.
    // The summary endpoint can be slow (it may fan out to edge), so do not block the header on it.
    await fetchDevice({ manageInitialLoad: false });
    if (initialLoadRef.current) {
      setLoading(false);
      initialLoadRef.current = false;
    }

    try {
      const res = await api.get<DeviceControlSummary>(
        `/api/v1/device_control/devices/${encodeURIComponent(deviceId)}/summary`
      );
      applyBootstrapSummary(res.data || {});
      setError(null);
    } catch (err: any) {
      console.warn('Could not load DeviceControl bootstrap summary; continuing with per-endpoint refresh:', err);
    }
  }, [deviceId, applyBootstrapSummary, fetchDevice]);

  const handleSelectControlDevice = useCallback((nextRouteDeviceId: string) => {
    const nextId = String(nextRouteDeviceId || '').trim();
    if (!nextId || nextId === deviceId) return;
    const matched = controlDeviceOptions.find((item) => item.id === nextId);
    if (matched?.device_id) {
      try {
        localStorage.setItem('ui.last_device_id', String(matched.device_id));
      } catch {
        // Best-effort only.
      }
    }
    try {
      if (looksLikeDeviceRouteUuid(nextId)) {
        localStorage.setItem(LAST_DEVICE_CONTROL_ROUTE_KEY, nextId);
      }
    } catch {
      // Best-effort only.
    }
    navigate(`/devices/${encodeURIComponent(nextId)}/control`);
  }, [controlDeviceOptions, deviceId, navigate]);

  const latestIncidentSummary = bootstrapSummary?.latest_incident || null;
  const latestIncidentLabel = useMemo(
    () => deviceControlIncidentLabel(latestIncidentSummary),
    [latestIncidentSummary]
  );
  const latestIncidentEvidenceState = useMemo(
    () => deviceControlIncidentEvidenceState(latestIncidentSummary),
    [latestIncidentSummary]
  );
  const latestIncidentProofScopeId = useMemo(
    () => deviceControlIncidentProofScopeId(latestIncidentSummary),
    [latestIncidentSummary]
  );
  const latestIncidentSourceContext = useMemo(
    () => deviceControlIncidentSourceContext(latestIncidentSummary),
    [latestIncidentSummary]
  );
  const latestIncidentTimestampLabel = useMemo(() => {
    const ts = String(latestIncidentSummary?.created_at || '').trim();
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  }, [latestIncidentSummary]);

  // Fetch pipeline status
  const fetchPipelineStatus = useCallback(async () => {
    if (!pipelineDeviceId || pipelineStatusInFlightRef.current) return;

    try {
      pipelineStatusInFlightRef.current = true;
      const token = getStoredAccessToken();
      const response = await fetch(`/api/v1/pipeline/devices/${pipelineDeviceId}/pipeline/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (response.ok) {
        const next = await response.json();
        setPipelineStatus((prev) => {
          // Keep it cheap: compare the fields we actually render frequently
          if (
            prev &&
            prev.state === next.state &&
            prev.pipeline_name === next.pipeline_name &&
            prev.uptime_seconds === next.uptime_seconds &&
            prev.timestamp === next.timestamp
          ) {
            return prev;
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to fetch pipeline status:', err);
    } finally {
      pipelineStatusInFlightRef.current = false;
    }
  }, [pipelineDeviceId]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    if (!pipelineDeviceId || metricsInFlightRef.current) return;

    try {
      metricsInFlightRef.current = true;
      const token = getStoredAccessToken();
      const response = await fetch(`/api/v1/pipeline/devices/${pipelineDeviceId}/metrics`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        const data = await response.json();
        setMetrics((prev) => {
          // Preserve previous values if the endpoint omits a field (prevents flicker to 0).
          const next = {
            fps: typeof data.fps === 'number' ? data.fps : prev.fps,
            latency_ms: typeof data.latency_ms === 'number' ? data.latency_ms : prev.latency_ms,
            cpu_percent: typeof data.cpu_percent === 'number' ? data.cpu_percent : prev.cpu_percent,
            memory_percent: typeof data.memory_percent === 'number' ? data.memory_percent : prev.memory_percent,
            gpu_percent: undefined,
            temperature_c:
              typeof data.temperature_c === 'number'
                ? data.temperature_c
                : data.temperature_c === null
                  ? null
                  : prev.temperature_c,
            thermal_status: typeof data.thermal_status === 'string' ? data.thermal_status : prev.thermal_status,
            detections_per_second: typeof data.detections_per_second === 'number' ? data.detections_per_second : prev.detections_per_second,
            alerts_total: prev.alerts_total,
            uptime_hours: typeof data.uptime_seconds === 'number' ? data.uptime_seconds / 3600 : prev.uptime_hours,
          };
          if (
            prev.fps === next.fps &&
            prev.latency_ms === next.latency_ms &&
            prev.cpu_percent === next.cpu_percent &&
            prev.memory_percent === next.memory_percent &&
            prev.temperature_c === next.temperature_c &&
            prev.thermal_status === next.thermal_status &&
            prev.detections_per_second === next.detections_per_second &&
            prev.uptime_hours === next.uptime_hours
          ) {
            return prev;
          }
          return next;
        });
        setMetricsLoaded(true);
      }
    } catch (err) {
      console.debug('Could not fetch device metrics:', err);
    } finally {
      metricsInFlightRef.current = false;
    }
  }, [pipelineDeviceId]);

  const fetchRegistryCameras = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!device?.device_id) return;
      const silent = !!opts?.silent;

      // Only show "loading" (and therefore disable buttons) during initial load
      // or explicit actions. Background polling should be silent to avoid flicker.
      const showLoading = !silent && !registryLoadedRef.current;
      if (showLoading) setRegistryLoading(true);

      try {
        const res = await api.get(`/api/v1/registry/devices/${device.device_id}/cameras`);
        const next: RegistryCamera[] = res.data?.items || [];
        registryLoadedRef.current = true;

        setRegistryCameras((prev) => {
          if (prev.length === next.length) {
            let same = true;
            for (let i = 0; i < prev.length; i++) {
              const a = prev[i];
              const b = next[i];
              const ah = (a as any)?.health || {};
              const bh = (b as any)?.health || {};
              if (
                a.camera_id !== b.camera_id ||
                (a.display_name || '') !== (b.display_name || '') ||
                (a.slug || '') !== (b.slug || '') ||
                (ah.status || '') !== (bh.status || '') ||
                (ah.last_test_at || '') !== (bh.last_test_at || '') ||
                (ah.last_error || '') !== (bh.last_error || '')
              ) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return next;
        });
      } catch (err) {
        // Best-effort: keep UI usable even if registry isn't available.
        console.debug('Could not fetch registry cameras', err);
      } finally {
        if (showLoading) setRegistryLoading(false);
      }
    },
    [device?.device_id]
  );

  const fetchRegistryPipelines = useCallback(
    async (_opts?: { silent?: boolean }) => {
      if (!device?.device_id) return;
      try {
        const res = await api.get(`/api/v1/registry/devices/${device.device_id}/pipelines`);
        const data: RegistryPipelinesResponse = res.data || {};
        const items = data.cameras || [];
        const byCam: Record<
          string,
          {
            pipeline?: string;
            alive?: boolean;
            runtime_desired_state?: string | null;
            runtime_override?: any;
            source_reachable?: boolean | null;
            source_error?: string | null;
            meta_path?: string | null;
            log_path?: string | null;
            log_tail?: string[] | null;
            log_tail_truncated?: boolean | null;
            last_error_line?: string | null;
          }
        > = {};
        for (const c of items) {
          if (c?.camera_id)
            byCam[c.camera_id] = {
              pipeline: c.pipeline,
              alive: c.alive,
              runtime_desired_state: c.runtime_desired_state ?? null,
              runtime_override: c.runtime_override,
              source_reachable: (c as any)?.source_reachable ?? null,
              source_error: (c as any)?.source_error ?? null,
              meta_path: (c as any)?.meta_path ?? null,
              log_path: (c as any)?.log_path ?? null,
              log_tail: (c as any)?.log_tail ?? null,
              log_tail_truncated: (c as any)?.log_tail_truncated ?? null,
              last_error_line: (c as any)?.last_error_line ?? null,
            };
        }
        setRegistryPipelines((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(byCam);
          if (prevKeys.length === nextKeys.length) {
            let same = true;
            for (const k of nextKeys) {
              const prevOv = (prev[k]?.runtime_override || {}) as any;
              const nextOv = (byCam[k]?.runtime_override || {}) as any;
              if (
                prev[k]?.pipeline !== byCam[k]?.pipeline ||
                prev[k]?.alive !== byCam[k]?.alive ||
                (prev[k]?.runtime_desired_state ?? null) !== (byCam[k]?.runtime_desired_state ?? null) ||
                // IMPORTANT: display mode changes are stored inside runtime_override; include them or the UI will
                // never reflect a mode change (it will appear to snap back to "Auto").
                String(prevOv?.pipeline_display_mode || '') !== String(nextOv?.pipeline_display_mode || '')
              ) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return byCam;
        });
      } catch (err) {
        // Best-effort; chips will fall back to heuristic.
        console.debug('Could not fetch registry pipelines', err);
      }
    },
    [device?.device_id]
  );

  const fetchEdgePipelinesRuntime = useCallback(
    async (_opts?: { silent?: boolean }) => {
      if (!device?.device_id) return;
      try {
        const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/pipelines/cameras/runtime`);
        const cams = (res?.data?.cameras ?? res?.data?.items ?? []) as any[];
        const byCam: Record<
          string,
          { pipeline?: string; alive?: boolean; log_tail?: string[] | null; models_in_use?: any[] | null }
        > = {};
        for (const c of cams) {
          if (!c?.camera_id) continue;
          byCam[String(c.camera_id)] = {
            pipeline: c.pipeline,
            alive: c.alive,
            log_tail: (c as any)?.log_tail ?? null,
            models_in_use: (c as any)?.models_in_use ?? null,
          };
        }
        setEdgePipelinesRuntime(byCam);
      } catch (err) {
        // Best-effort: don't break Models panel if edge is unreachable.
        console.debug('Could not fetch edge pipelines runtime', err);
      }
    },
    [device?.device_id]
  );

  const fetchRegistryPolicies = useCallback(
    async (_opts?: { silent?: boolean }) => {
      if (!device?.device_id) return;
      try {
        const res = await api.get(`/api/v1/registry/devices/${device.device_id}/policies`);
        const items: RegistryPolicy[] = (res.data?.policies || []).map((p: any) => ({
          policy_id: p.policy_id,
          device_id: p.device_id,
          camera_id: p.camera_id ?? null,
          policy_type: p.policy_type,
          display_name: p.display_name ?? null,
          enabled: p.enabled,
          config: p.config,
        }));

        const byCam: Record<string, RegistryPolicy[]> = {};
        const deviceWide: RegistryPolicy[] = [];
        for (const p of items) {
          if (!p) continue;
          if (!p.camera_id) {
            deviceWide.push(p);
            continue;
          }
          byCam[p.camera_id] = byCam[p.camera_id] || [];
          byCam[p.camera_id].push(p);
        }

        // Keep updates diff-aware to avoid flicker.
        setRegistryDevicePolicies((prev) => {
          if (prev.length === deviceWide.length) {
            let same = true;
            for (let i = 0; i < prev.length; i++) {
              const a = prev[i];
              const b = deviceWide[i];
              if (a.policy_id !== b.policy_id || a.policy_type !== b.policy_type) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return deviceWide;
        });
        setRegistryPoliciesByCamera((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(byCam);
          if (prevKeys.length === nextKeys.length) {
            let same = true;
            for (const k of nextKeys) {
              const a = prev[k] || [];
              const b = byCam[k] || [];
              if (a.length !== b.length) {
                same = false;
                break;
              }
              for (let i = 0; i < a.length; i++) {
                if (a[i].policy_id !== b[i].policy_id || a[i].policy_type !== b[i].policy_type) {
                  same = false;
                  break;
                }
              }
              if (!same) break;
            }
            if (same) return prev;
          }
          return byCam;
        });
      } catch (err) {
        console.debug('Could not fetch registry policies', err);
      }
    },
    [device?.device_id]
  );

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!pipelineDeviceId) return;

    setLogsLoading(true);
    try {
      const token = getStoredAccessToken();
      const response = await fetch(`/api/v1/pipeline/devices/${pipelineDeviceId}/edge/logs/tail?lines=100`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: AbortSignal.timeout(10000),
      });

      if (response.ok) {
        const data = await response.json();
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        if (entries.length > 0) {
          const sourceLabel =
            data?.program || (data?.mode === 'docker' ? (data?.container || 'docker') : (data?.mode || 'edge'));
          setLogs(
            entries.map((entry: any) => ({
              source: String(sourceLabel || 'edge'),
              message: String(entry?.raw || ''),
              timestamp: '',
            }))
          );
        } else if (data?.error || data?.detail) {
          setLogs([
            {
              source: 'system',
              message: `${data?.error || 'edge_logs_failed'}${data?.detail ? `: ${data.detail}` : ''}`,
              timestamp: '',
            },
          ]);
        } else {
          setLogs([]);
        }
        setTimeout(() => {
          logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return;
      }

      // Backwards-compat fallback for older deployments still using the MQTT log collector path.
      const fallbackResponse = await fetch(`/api/v1/pipeline/devices/${pipelineDeviceId}/logs?lines=100`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: AbortSignal.timeout(10000),
      });
      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        setLogs(fallbackData.logs || []);
      }
    } catch (err) {
      console.debug('Could not fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [pipelineDeviceId]);

  const fetchIncidentHealth = useCallback(async () => {
    if (!pipelineDeviceId) return;
    setIncidentHealthLoading(true);
    try {
      const token = getStoredAccessToken();
      const response = await fetch(`/api/v1/pipeline/devices/${pipelineDeviceId}/health/incident_e2e`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: AbortSignal.timeout(6000),
      });
      if (response.ok) {
        const data = await response.json();
        setIncidentHealth(data);
      } else {
        setIncidentHealth({ ok: false, error: `HTTP ${response.status}` });
      }
    } catch (err: any) {
      setIncidentHealth({ ok: false, error: err?.message || 'unreachable' });
    } finally {
      setIncidentHealthLoading(false);
    }
  }, [pipelineDeviceId]);

  const fetchRunnerLatest = useCallback(async () => {
    if (!device?.device_id) return;
    setRunnerLoading(true);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/runner/runs/latest`);
      setRunnerLatest(res.data || null);
    } catch (err: any) {
      setRunnerLatest({ ok: false, error: err?.response?.data?.error || err?.message || 'unreachable' });
    } finally {
      setRunnerLoading(false);
    }
  }, [device?.device_id]);

  const fetchRunnerRun = useCallback(async (runId: string) => {
    if (!device?.device_id || !runId) return;
    setRunnerLoading(true);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/runner/runs/${runId}`);
      setRunnerLatest(res.data || null);
    } catch (err: any) {
      setRunnerLatest((prev: any) => prev || { ok: false, error: err?.response?.data?.error || err?.message || 'unreachable' });
    } finally {
      setRunnerLoading(false);
    }
  }, [device?.device_id]);

  const runProvisioner = async (mode: 'apply_and_verify' | 'verify_only') => {
    if (!device?.device_id) return;
    setRunnerLoading(true);
    try {
      const params: Record<string, any> = {};
      if (runnerUseLocalRepo) {
        params.use_local_repo = true;
        if (runnerRepoDir.trim()) params.repo_dir = runnerRepoDir.trim();
      }
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/runner/runs`, {
        mode,
        components: ['native', 'docker'],
        strict: true,
        ...(Object.keys(params).length > 0 ? { params } : {}),
      });
      setRunnerLatest(res.data || null);
      setRunnerDialogRunId(String(res?.data?.run_id || ''));
      setSnackbar({ open: true, message: `Runner started (${mode})`, severity: 'info' });
      setRunnerDialogOpen(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Runner start failed', severity: 'error' });
    } finally {
      setRunnerLoading(false);
    }
  };

  const cancelProvisionerRun = async () => {
    if (!device?.device_id || !runnerLatest?.run_id) return;
    setRunnerLoading(true);
    try {
      const res = await api.post(
        `/api/v1/pipeline/devices/${device.device_id}/runner/runs/${runnerLatest.run_id}/cancel`
      );
      setRunnerLatest((prev: any) => ({
        ...(prev || {}),
        ...(res?.data || {}),
      }));
      await fetchRunnerLatest();
      await fetchRunnerLogs();
      setSnackbar({ open: true, message: `Runner cancelled (${runnerLatest.run_id})`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || err?.message || 'Runner cancel failed',
        severity: 'error',
      });
    } finally {
      setRunnerLoading(false);
    }
  };

  const runRunnerMode = async (mode: 'activate_release' | 'verify_release', params: any = {}) => {
    if (!device?.device_id) return;
    setRunnerLoading(true);
    try {
      const nextParams: Record<string, any> = { ...(params || {}) };
      if (runnerUseLocalRepo) {
        nextParams.use_local_repo = true;
        if (runnerRepoDir.trim()) nextParams.repo_dir = runnerRepoDir.trim();
      }
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/runner/runs`, {
        mode,
        components: ['native', 'docker'],
        strict: true,
        params: nextParams,
      });
      setRunnerLatest(res.data || null);
      setRunnerDialogRunId(String(res?.data?.run_id || ''));
      setSnackbar({ open: true, message: `Runner started (${mode})`, severity: 'info' });
      setRunnerDialogOpen(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Runner start failed', severity: 'error' });
    } finally {
      setRunnerLoading(false);
    }
  };

  const fetchReleaseStatus = useCallback(async (arg?: any) => {
    if (!device?.device_id) return;
    const opts = arg && typeof arg === 'object' && 'silent' in arg ? (arg as { silent?: boolean }) : undefined;
    if (!opts?.silent) setReleaseLoading(true);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/status`);
      setReleaseStatus(res.data || null);
      return res.data || null;
    } catch (err: any) {
      const next = { ok: false, error: err?.response?.data?.error || err?.message || 'unreachable' };
      setReleaseStatus(next);
      return next;
    } finally {
      if (!opts?.silent) setReleaseLoading(false);
    }
  }, [device?.device_id]);

  const fetchReleasesCatalog = useCallback(async () => {
    setReleasesCatalogLoading(true);
    try {
      const res = await api.get(`/api/v1/releases`);
      setReleasesCatalog(Array.isArray(res?.data?.releases) ? res.data.releases : []);
    } catch (_err: any) {
      setReleasesCatalog([]);
    } finally {
      setReleasesCatalogLoading(false);
    }
  }, []);

  const fetchDevOtaBuildEnabled = useCallback(async () => {
    try {
      const res = await api.get(`/api/v1/releases/dev/build/enabled`);
      setDevOtaBuildEnabled(Boolean(res?.data?.enabled));
    } catch (_e) {
      setDevOtaBuildEnabled(false);
    }
  }, []);

  const fetchDevOtaBuildStatus = useCallback(async (jobId: string) => {
    const jid = String(jobId || '').trim();
    if (!jid) return null;
    const res = await api.get(`/api/v1/releases/dev/build/status`, { params: { job_id: jid } });
    const data = res?.data || null;
    setDevOtaBuildJob(data);
    return data;
  }, []);

  const fetchDevOtaBuildLogs = useCallback(async (jobId: string) => {
    const jid = String(jobId || '').trim();
    if (!jid) return;
    try {
      const res = await api.get(`/api/v1/releases/dev/build/logs`, { params: { job_id: jid, tail: 8000 } });
      const txt = String(res?.data?.logs || '');
      setDevOtaBuildLogs(txt);
    } catch (_e) {
      // ignore
    }
  }, []);

  const stopDevOtaBuildPolling = useCallback(() => {
    if (devOtaBuildPollRef.current) {
      clearInterval(devOtaBuildPollRef.current);
      devOtaBuildPollRef.current = null;
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for dev OTA UI
  const startDevOtaBuild = useCallback(async () => {
    setDevOtaBuildLoading(true);
    try {
      const tag = `ota_dev_${Date.now()}_arm64`;
      const res = await api.post(`/api/v1/releases/dev/build/start`, { tag, platform: 'linux/arm64' });
      const job = res?.data || null;
      setDevOtaBuildJob(job);
      if (!job?.job_id) {
        setSnackbar({ open: true, message: job?.error || 'Dev OTA build start failed', severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `Dev OTA build started (job=${job.job_id})`, severity: 'info' });

      stopDevOtaBuildPolling();
      devOtaBuildPollRef.current = setInterval(async () => {
        try {
          const st = await fetchDevOtaBuildStatus(job.job_id);
          await fetchDevOtaBuildLogs(job.job_id);
          const status = String(st?.status || '').toLowerCase();
          if (status && status !== 'running') {
            stopDevOtaBuildPolling();
            if (status === 'ok' && st?.digest_ref && st?.tag) {
              // Auto-register release in cloud DB for convenience (dev).
              try {
                const create = await api.post(`/api/v1/releases`, {
                  release_id: String(st.tag),
                  image_digest_ref: String(st.digest_ref),
                  notes: 'dev UI build',
                });
                if (create?.data?.ok) {
                  setSnackbar({ open: true, message: `Release published: ${String(create.data.release_id)}`, severity: 'success' });
                  await fetchReleasesCatalog();
                  setDesiredReleaseId(String(create.data.release_id));
                  setDesiredReleaseDirty(true);
                } else {
                  setSnackbar({ open: true, message: create?.data?.error || 'Release publish failed', severity: 'error' });
                }
              } catch (e: any) {
                setSnackbar({ open: true, message: e?.response?.data?.error || 'Release publish failed', severity: 'error' });
              }
            } else if (status === 'error') {
              setSnackbar({ open: true, message: `Dev OTA build failed: ${String(st?.detail || st?.error || '')}`.slice(0, 180), severity: 'error' });
            }
          }
        } catch (_e) {
          // ignore transient poll errors
        }
      }, 4000);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || err?.message || 'Dev OTA build start failed', severity: 'error' });
    } finally {
      setDevOtaBuildLoading(false);
    }
  }, [fetchDevOtaBuildLogs, fetchDevOtaBuildStatus, fetchReleasesCatalog, stopDevOtaBuildPolling]);

  const verifyOtaUpdate = useCallback(
    async (arg?: any) => {
      // Allow using as an onClick handler (MouseEvent passed in).
      void arg;
      if (!device?.device_id) return;
      const target = String(desiredReleaseId || '').trim();
      if (!target) {
        setSnackbar({ open: true, message: 'Select a desired release_id first', severity: 'error' });
        return;
      }
      setOtaVerifyLoading(true);
      try {
        const rel = await fetchReleaseStatus({ silent: true });
        const current = String((rel as any)?.current?.release_id || '').trim();
        const stState = String((rel as any)?.status?.state || '').toUpperCase();
        const lastState = String((rel as any)?.last_attempt?.state || '').toUpperCase();
        const lastErr = String((rel as any)?.last_attempt?.error || (rel as any)?.status?.detail?.error || '').trim();

        if (stState === 'RUNNING' && current === target) {
          setSnackbar({ open: true, message: `✅ Update successful. current=${current}`, severity: 'success' });
          return;
        }
        if (lastState === 'FAILED' || stState === 'FAILED') {
          setSnackbar({
            open: true,
            message: `❌ Update failed${lastErr ? `: ${lastErr}` : ''}`.slice(0, 200),
            severity: 'error',
          });
          return;
        }
        if (stState === 'ACTIVATING' || stState === 'STABILIZING' || lastState === 'ACTIVATING' || lastState === 'STABILIZING') {
          setSnackbar({ open: true, message: `⏳ Update still running. state=${stState || lastState || 'unknown'}`, severity: 'info' });
          return;
        }
        // Fallback
        setSnackbar({
          open: true,
          message: `Update not confirmed yet. desired=${target} current=${current || 'unknown'} state=${stState || 'unknown'}`,
          severity: 'info',
        });
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.response?.data?.error || err?.message || 'Verify failed', severity: 'error' });
      } finally {
        setOtaVerifyLoading(false);
      }
    },
    [device?.device_id, desiredReleaseId, fetchReleaseStatus],
  );

  const fetchDesiredRelease = useCallback(async () => {
    if (!device?.device_id) return;
    setDesiredReleaseLoading(true);
    try {
      const res = await api.get(`/api/v1/devices/${device.device_id}/desired_release`);
      const data = res?.data || null;
      setDesiredReleaseInfo(data);
      const nextId = String(data?.desired_release_id || '').trim();
      // Avoid clobbering a user's in-progress selection with background polling.
      if (!desiredReleaseDirty) setDesiredReleaseId(nextId);
    } catch (err: any) {
      setDesiredReleaseInfo({ ok: false, error: err?.response?.data?.error || err?.message || 'unreachable' });
    } finally {
      setDesiredReleaseLoading(false);
    }
  }, [device?.device_id, desiredReleaseDirty]);

  const setDesiredRelease = useCallback(async () => {
    if (!device?.device_id) return;
    setDesiredReleaseLoading(true);
    try {
      const payload: any = { desired_release_id: desiredReleaseId.trim() || null };
      const res = await api.put(`/api/v1/devices/${device.device_id}/desired_release`, payload);
      if (res?.data?.ok) {
        setSnackbar({ open: true, message: 'Desired release updated', severity: 'success' });
        setDesiredReleaseDirty(false);
        fetchDesiredRelease();
      } else {
        setSnackbar({ open: true, message: res?.data?.error || 'Failed to update desired release', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to update desired release', severity: 'error' });
    } finally {
      setDesiredReleaseLoading(false);
    }
  }, [device?.device_id, desiredReleaseId, fetchDesiredRelease]);

  const fetchAgentStatus = useCallback(async (arg?: any) => {
    if (!device?.device_id) return;
    const opts = arg && typeof arg === 'object' && 'silent' in arg ? (arg as { silent?: boolean }) : undefined;
    if (!opts?.silent) setAgentStatusLoading(true);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/agent/status`);
      setAgentStatus(res?.data || null);
      return res?.data || null;
    } catch (err: any) {
      const next = { ok: false, error: err?.response?.data?.error || err?.message || 'unreachable' };
      setAgentStatus(next);
      return next;
    } finally {
      if (!opts?.silent) setAgentStatusLoading(false);
    }
  }, [device?.device_id]);

  const fetchDesiredModels = useCallback(
    async (arg?: any) => {
      if (!device?.device_id) return;
      const opts = arg && typeof arg === 'object' && 'silent' in arg ? (arg as { silent?: boolean }) : undefined;
      if (!opts?.silent) setDesiredModelsLoading(true);
      try {
        const res = await api.get(`/api/v1/devices/${device.device_id}/desired_models`);
        setDesiredModels(res?.data || null);
        setDesiredModelsError(null);
        return res?.data || null;
      } catch (err: any) {
        const msg = String(err?.response?.data?.error || err?.message || 'unreachable');
        setDesiredModelsError(msg);
        return null;
      } finally {
        if (!opts?.silent) setDesiredModelsLoading(false);
      }
    },
    [device?.device_id]
  );

  const fetchDesiredStateProof = useCallback(
    async (arg?: any) => {
      if (!device?.device_id) return;
      const opts = arg && typeof arg === 'object' && 'silent' in arg ? (arg as { silent?: boolean }) : undefined;
      if (!opts?.silent) setDesiredStateProofLoading(true);
      try {
        const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/desired_state/status`);
        setDesiredStateProof(res?.data || null);
        return res?.data || null;
      } catch (_err: any) {
        // Best-effort: keep UI usable even if edge is unreachable.
        return null;
      } finally {
        if (!opts?.silent) setDesiredStateProofLoading(false);
      }
    },
    [device?.device_id]
  );

  const fetchModelsCatalog = useCallback(async () => {
    setModelsCatalogLoading(true);
    try {
      const res = await api.get('/api/v1/models');
      const list = (res?.data?.models ?? []) as any[];
      setModelsCatalog(list);
      return list;
    } catch (_err: any) {
      setModelsCatalog([]);
      return [];
    } finally {
      setModelsCatalogLoading(false);
    }
  }, []);

  const handleOpenSetDesiredModels = useCallback(() => {
    setSetDesiredModelsDialogOpen(true);
    fetchModelsCatalog();
    // Prefill from current desired models
    const current = desiredModels?.models;
    if (Array.isArray(current) && current.length > 0) {
      setSelectedModelsForDesired(
        current.map((m: any) => ({
          model_id: String(m?.model_id ?? ''),
          name: String(m?.name ?? m?.model_id ?? ''),
          required: m?.required !== false,
          role: m?.role ? String(m.role) : undefined,
        }))
      );
    } else {
      setSelectedModelsForDesired([]);
    }
  }, [desiredModels?.models, fetchModelsCatalog]);

  const handleSaveDesiredModels = useCallback(async () => {
    if (!device?.device_id) return;
    setSetDesiredModelsSaving(true);
    try {
      const payload = selectedModelsForDesired
        .filter((m) => m.model_id && m.name)
        .map((m) => ({
          model_id: m.model_id,
          name: m.name,
          required: m.required,
          ...(m.role ? { role: m.role } : {}),
        }));
      await api.put(`/api/v1/devices/${device.device_id}/desired_models`, { models: payload });
      setSnackbar({ open: true, message: 'Desired models updated. Edge will sync within ~20s.', severity: 'success' });
      setSetDesiredModelsDialogOpen(false);
      fetchDesiredModels();
      fetchDesiredStateProof();
    } catch (err: any) {
      const msg = String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Failed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSetDesiredModelsSaving(false);
    }
  }, [device?.device_id, selectedModelsForDesired, fetchDesiredModels, fetchDesiredStateProof]);

  const handleClearDesiredModels = useCallback(async () => {
    if (!device?.device_id) return;
    if (!window.confirm('Clear all desired models for this device? The edge will treat models as satisfied.')) return;
    setSetDesiredModelsSaving(true);
    try {
      await api.put(`/api/v1/devices/${device.device_id}/desired_models`, { models: [] });
      setSnackbar({ open: true, message: 'Desired models cleared.', severity: 'success' });
      setSetDesiredModelsDialogOpen(false);
      fetchDesiredModels();
      fetchDesiredStateProof();
    } catch (err: any) {
      const msg = String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Failed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSetDesiredModelsSaving(false);
    }
  }, [device?.device_id, fetchDesiredModels, fetchDesiredStateProof]);

  const modelsInSync =
    typeof desiredStateProof?.domains?.models?.ok === 'boolean' &&
    desiredStateProof.domains.models.ok &&
    desiredModels?.desired_model_set_id &&
    desiredStateProof.domains.models.applied?.id === desiredModels.desired_model_set_id;

  const stopOtaPolling = useCallback(() => {
    if (otaPollIntervalRef.current) {
      clearInterval(otaPollIntervalRef.current);
      otaPollIntervalRef.current = null;
    }
    otaPollDeadlineRef.current = null;
    setOtaPolling(false);
  }, []);

  const startOtaPolling = useCallback(
    (targetReleaseId: string) => {
      const target = String(targetReleaseId || '').trim();
      if (!target) return;
      stopOtaPolling();
      setOtaPolling(true);
      otaPollDeadlineRef.current = Date.now() + 5 * 60 * 1000; // 5 minutes

      otaPollIntervalRef.current = setInterval(async () => {
        const dl = otaPollDeadlineRef.current;
        if (dl && Date.now() > dl) {
          stopOtaPolling();
          setSnackbar({ open: true, message: `OTA still running (timeout). Desired=${target}. Check Release Status / logs.`, severity: 'info' });
          return;
        }

        try {
          const [agent, rel] = await Promise.all([
            fetchAgentStatus({ silent: true }),
            fetchReleaseStatus({ silent: true }),
          ]);

          const agentStatusStr = String((agent as any)?.status || '').toLowerCase();
          const agentOk = (agent as any)?.ok;
          const currentRelease = String((rel as any)?.current?.release_id || '').trim();

          // Success criteria: edge reports current release == desired.
          if (currentRelease && currentRelease === target) {
            stopOtaPolling();
            setSnackbar({ open: true, message: `OTA complete. current=${currentRelease}`, severity: 'success' });
            return;
          }

          // Agent terminal states
          if (agentOk === false || agentStatusStr === 'error') {
            stopOtaPolling();
            const detail = String((agent as any)?.error || (agent as any)?.detail || 'agent error');
            setSnackbar({ open: true, message: `OTA failed: ${detail}`.slice(0, 180), severity: 'error' });
            return;
          }

          // If agent says ok + already at desired, treat as success even if release status is stale.
          if (agentStatusStr === 'ok' && String((agent as any)?.detail || '').toLowerCase().includes('already')) {
            stopOtaPolling();
            setSnackbar({ open: true, message: `OTA complete. ${target} already active.`, severity: 'success' });
          }
        } catch (_e) {
          // Ignore transient errors; polling will retry until deadline.
        }
      }, 4000);
    },
    [fetchAgentStatus, fetchReleaseStatus, stopOtaPolling],
  );

  const triggerAgentPollNow = useCallback(async () => {
    if (!device?.device_id) return;
    setAgentStatusLoading(true);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/agent/poll_now`, { reason: 'ui' });
      if (res?.data?.ok) {
        setSnackbar({ open: true, message: 'Release agent poll requested', severity: 'success' });
        const target = desiredReleaseId.trim();
        if (target) startOtaPolling(target);
      } else {
        setSnackbar({ open: true, message: res?.data?.error || 'Release agent poll request failed', severity: 'error' });
      }
      // Best-effort refresh
      setTimeout(() => fetchAgentStatus(), 800);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Release agent poll request failed', severity: 'error' });
    } finally {
      setAgentStatusLoading(false);
    }
  }, [device?.device_id, desiredReleaseId, fetchAgentStatus, startOtaPolling]);

  const runOtaPrecheck = useCallback(
    async (opts?: { autoFix?: boolean; silent?: boolean; pullTimeoutSec?: number }) => {
      if (!device?.device_id) return null;
      const autoFix = Boolean(opts?.autoFix);
      const silent = Boolean(opts?.silent);
      const pullTimeoutSec = Number.isFinite(opts?.pullTimeoutSec as any) ? Number(opts?.pullTimeoutSec) : undefined;
      if (!silent) setOtaPrecheckLoading(true);
      try {
        const target = desiredReleaseId.trim();
        const digestRef = String(desiredReleaseInfo?.desired_release?.image_digest_ref || '').trim() || null;
        const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/precheck`, {
          desired_release_id: target || null,
          image_digest_ref: digestRef,
          expected_arch: 'arm64',
          auto_fix: autoFix,
          timeout_sec: 25,
          // Pulling large images can take longer than other checks.
          pull_timeout_sec: pullTimeoutSec ?? (autoFix ? 180 : 60),
        });
        const data = res?.data || null;
        setOtaPrecheckResult(data);
        if (!silent) {
          const st = String(data?.status || '').toLowerCase();
          if (st === 'ok_with_warnings') {
            setSnackbar({ open: true, message: '⚠️ OTA precheck passed with warnings (see details)', severity: 'warning' });
          } else if (data?.ok) {
            setSnackbar({ open: true, message: '✅ OTA precheck passed', severity: 'success' });
          } else {
            setSnackbar({ open: true, message: '❌ OTA precheck failed (see details)', severity: 'error' });
          }
        }
        return data;
      } catch (err: any) {
        const next = { ok: false, error: err?.response?.data?.error || err?.message || 'precheck_failed' };
        setOtaPrecheckResult(next);
        if (!silent) setSnackbar({ open: true, message: next.error || 'Precheck failed', severity: 'error' });
        return next;
      } finally {
        if (!silent) setOtaPrecheckLoading(false);
      }
    },
    [device?.device_id, desiredReleaseId, desiredReleaseInfo],
  );

  const fetchReleaseAttempts = useCallback(async () => {
    if (!device?.device_id) return;
    setReleaseAttemptsLoading(true);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/attempts`, { params: { limit: 20 } });
      const rows = Array.isArray(res?.data?.attempts) ? res.data.attempts : [];
      setReleaseAttempts(rows);
      setReleaseAttemptsOpen(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to load attempt history', severity: 'error' });
    } finally {
      setReleaseAttemptsLoading(false);
    }
  }, [device?.device_id]);

  const runCompleteUpdate = useCallback(async () => {
    if (!device?.device_id) return;
    // Pilot default: agent-driven OTA only (no SSH/push update). Native update is break-glass only.
    const targetRelease = desiredReleaseId.trim();
    if (!targetRelease) {
      setSnackbar({ open: true, message: 'Select a desired release_id first (or choose none to disable OTA).', severity: 'error' });
      return;
    }
    setDesiredReleaseLoading(true);
    setAgentStatusLoading(true);
    try {
      // 0) Precheck (block early to avoid predictable failures mid-deploy)
      let pre = await runOtaPrecheck({ silent: true, pullTimeoutSec: 120 });
      if (!pre?.ok) {
        // One-click prep: try safe auto-fix once, then recheck.
        pre = await runOtaPrecheck({ silent: true, autoFix: true, pullTimeoutSec: 180 });
      }
      if (!pre?.ok) {
        setSnackbar({ open: true, message: 'OTA precheck failed — not starting update. Use “Prepare for OTA” and retry.', severity: 'error' });
        setOtaPrecheckResult(pre);
        return;
      }

      // 1) Persist desired_release in cloud
      const setRes = await api.put(`/api/v1/devices/${device.device_id}/desired_release`, { desired_release_id: targetRelease });
      if (!setRes?.data?.ok) {
        setSnackbar({ open: true, message: setRes?.data?.error || 'Failed to set desired release', severity: 'error' });
        return;
      }

      // 2) Trigger one-shot release agent poll (docker pull + activate)
      const poll = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/agent/poll_now`, { reason: 'complete_update' });
      if (!poll?.data?.ok) {
        setSnackbar({ open: true, message: poll?.data?.error || 'OTA trigger failed (agent)', severity: 'error' });
        return;
      }
      startOtaPolling(targetRelease);

      // 3) Best-effort desired-state sync (config drift healing)
      try {
        await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/desired_state/sync`, {});
      } catch (_e) {
        // ignore; not critical to the software update itself
      }

      // Refresh panels
      fetchDesiredRelease();
      fetchAgentStatus();
      fetchReleaseStatus();
      fetchReleaseAttempts();
      setSnackbar({ open: true, message: 'OTA requested. Watch agent + release status.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || err?.message || 'Update failed', severity: 'error' });
    } finally {
      setDesiredReleaseLoading(false);
      setAgentStatusLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connectionSettings/device.ip_address intentionally excluded
  }, [device?.device_id, desiredReleaseId, fetchAgentStatus, fetchDesiredRelease, fetchReleaseAttempts, fetchReleaseStatus, runOtaPrecheck, startOtaPolling]);

  const runRollbackToKnownGood = useCallback(async () => {
    if (!device?.device_id) return;
    if (otaPolling) return;
    setDesiredReleaseLoading(true);
    setAgentStatusLoading(true);
    try {
      const stRes = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/status`);
      const rel = stRes?.data || {};
      const current = String(rel?.current?.release_id || '').trim();
      const kgr = String(rel?.kgr?.release_id || '').trim();
      const prev = String(rel?.current?.previous_release_id || '').trim();
      const target = kgr || prev;

      if (!target) {
        setSnackbar({ open: true, message: 'No known-good/previous release available to roll back to', severity: 'error' });
        return;
      }
      if (current && target === current) {
        setSnackbar({ open: true, message: `Already on ${target}`, severity: 'info' });
        return;
      }

      // Set desired release to the rollback target
      const setRes = await api.put(`/api/v1/devices/${device.device_id}/desired_release`, { desired_release_id: target });
      if (!setRes?.data?.ok) {
        setSnackbar({ open: true, message: setRes?.data?.error || 'Failed to set desired release for rollback', severity: 'error' });
        return;
      }
      setDesiredReleaseId(target);
      setDesiredReleaseDirty(false);

      // Trigger agent poll to pull+activate
      const poll = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/agent/poll_now`, { reason: 'rollback_known_good' });
      if (!poll?.data?.ok) {
        setSnackbar({ open: true, message: poll?.data?.error || 'Rollback trigger failed (agent)', severity: 'error' });
        return;
      }
      startOtaPolling(target);

      // Best-effort drift healing
      try {
        await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/desired_state/sync`, {});
      } catch (_e) {
        // ignore; not critical to rollback request
      }

      fetchDesiredRelease();
      fetchAgentStatus();
      fetchReleaseStatus();
      fetchReleaseAttempts();
      setSnackbar({ open: true, message: `Rollback requested (OTA). Target=${target}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || err?.message || 'Rollback failed', severity: 'error' });
    } finally {
      setDesiredReleaseLoading(false);
      setAgentStatusLoading(false);
    }
  }, [device?.device_id, otaPolling, fetchAgentStatus, fetchDesiredRelease, fetchReleaseAttempts, fetchReleaseStatus, startOtaPolling]);

  const generateBaselineManifest = async () => {
    if (!device?.device_id) return;
    setReleaseLoading(true);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/baseline/create`, {
        runtime_mode: 'auto',
      });
      const manifest = res?.data?.manifest;
      if (manifest && typeof manifest === 'object') {
        setReleaseManifestText(JSON.stringify(manifest, null, 2));
        setSnackbar({ open: true, message: 'Baseline manifest generated from device', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Baseline manifest generation failed', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Baseline manifest generation failed', severity: 'error' });
    } finally {
      setReleaseLoading(false);
    }
  };

  const installReleaseBundle = async () => {
    if (!device?.device_id) return;
    const p = (releaseBundlePath || '').trim();
    if (!p) {
      setSnackbar({ open: true, message: 'Bundle path is required', severity: 'error' });
      return;
    }
    setReleaseLoading(true);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/bundle/install`, {
        bundle_path: p,
      });
      if (res?.data?.ok) {
        const installedReleaseId = typeof res?.data?.release_id === 'string' ? res.data.release_id : '';
        if (res?.data?.manifest && typeof res.data.manifest === 'object') {
          setReleaseManifestText(JSON.stringify(res.data.manifest, null, 2));
        } else {
          // Fallback: fetch list and locate the installed manifest
          try {
            const listRes = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/list`);
            const releases = listRes?.data?.releases || [];
            const found = releases.find((r: any) => r?.release_id === installedReleaseId) || releases[releases.length - 1];
            if (found?.manifest) setReleaseManifestText(JSON.stringify(found.manifest, null, 2));
          } catch (_e) {
            // ignore fallback errors
          }
        }
        setSnackbar({ open: true, message: 'Bundle installed on device', severity: 'success' });
        fetchReleaseStatus();
      } else {
        setSnackbar({ open: true, message: res?.data?.error || 'Bundle install failed', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Bundle install failed', severity: 'error' });
    } finally {
      setReleaseLoading(false);
    }
  };

  const buildReleaseBundle = async () => {
    if (!device?.device_id) return;
    setReleaseLoading(true);
    try {
      const suggestedReleaseId = `ui_bundle_${Date.now()}`;
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/bundle/build`, {
        runtime_mode: 'auto',
        release_id: suggestedReleaseId,
      });
      if (res?.data?.ok) {
        const builtPath = typeof res?.data?.bundle_path === 'string' ? res.data.bundle_path : '';
        if (builtPath) setReleaseBundlePath(builtPath);

        // Immediately install it so the manifest becomes activatable (native bundles need artifacts staged).
        if (builtPath) {
          // Always set the manifest from build response first (even before install) so the user never sees {}.
          if (res?.data?.manifest && typeof res.data.manifest === 'object') {
            setReleaseManifestText(JSON.stringify(res.data.manifest, null, 2));
          }
          const installed = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/release/bundle/install`, {
            bundle_path: builtPath,
          });
          if (installed?.data?.ok) {
            if (installed?.data?.manifest && typeof installed.data.manifest === 'object') {
              setReleaseManifestText(JSON.stringify(installed.data.manifest, null, 2));
            } else {
              // Fallback: fetch list and locate the installed manifest
              try {
                const installedReleaseId = typeof installed?.data?.release_id === 'string' ? installed.data.release_id : '';
                const listRes = await api.get(`/api/v1/pipeline/devices/${device.device_id}/edge/release/list`);
                const releases = listRes?.data?.releases || [];
                const found = releases.find((r: any) => r?.release_id === installedReleaseId) || releases[releases.length - 1];
                if (found?.manifest) setReleaseManifestText(JSON.stringify(found.manifest, null, 2));
              } catch (_e) {
                // ignore fallback errors
              }
            }
            setSnackbar({ open: true, message: installed?.data?.existing ? 'Bundle already installed' : 'Bundle built + installed on device', severity: 'success' });
            fetchReleaseStatus();
          } else {
            setSnackbar({ open: true, message: installed?.data?.error || 'Bundle install failed', severity: 'error' });
          }
        } else {
          // Fallback: show manifest if we have it.
          if (res?.data?.manifest && typeof res.data.manifest === 'object') {
            setReleaseManifestText(JSON.stringify(res.data.manifest, null, 2));
          }
          setSnackbar({ open: true, message: 'Bundle built on device (no path returned)', severity: 'success' });
        }
      } else {
        const errMsg = res?.data?.error === 'release_exists_conflict' ? 'Release ID conflict. Click again (it will generate a new id).' : (res?.data?.error || 'Bundle build failed');
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Bundle build failed', severity: 'error' });
    } finally {
      setReleaseLoading(false);
    }
  };

  const fetchRunnerLogs = useCallback(async (runId?: string) => {
    const targetRunId = runId || runnerDialogRunId || runnerLatest?.run_id;
    if (!device?.device_id || !targetRunId) return;
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${device.device_id}/runner/runs/${targetRunId}/logs?tail=500`);
      setRunnerLogs(res.data?.logs || []);
    } catch (err) {
      setRunnerLogs([]);
    }
  }, [device?.device_id, runnerDialogRunId, runnerLatest?.run_id]);

  const runnerSteps = Array.isArray(runnerLatest?.steps) ? runnerLatest.steps : [];
  const runnerFailedStep = [...runnerSteps].reverse().find((step: any) => step?.status === 'failed') || null;
  const runnerActiveStep = [...runnerSteps].reverse().find((step: any) => step?.status === 'running') || null;
  const runnerSummaryStep = runnerFailedStep || runnerActiveStep;

  const runnerStatusColor = (status?: string) => (
    status === 'succeeded'
      ? 'success'
      : status === 'failed'
      ? 'error'
      : status === 'running'
      ? 'warning'
      : 'default'
  );

  useEffect(() => {
    void fetchBootstrapSummary();
  }, [fetchBootstrapSummary]);

  useEffect(() => {
    if (!pipelineDeviceId) return;
    void fetchPipelineStatus();
    void fetchMetrics();

    // Device Control was polling every 7s, which is too aggressive for
    // constrained fanless edges because it repeatedly wakes the edge health
    // stack while the page is left open. Keep the initial fetch immediate,
    // then back off steady-state refreshes.
    const interval = setInterval(() => {
      if (!isDocumentVisible()) return;
      void fetchPipelineStatus();
      void fetchMetrics();
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, [
    pipelineDeviceId,
    fetchPipelineStatus,
    fetchMetrics,
  ]);

  useEffect(() => {
    if (!device?.device_id || tabValue !== 0) return;

    if (defaultTabDetailsTimerRef.current) {
      clearTimeout(defaultTabDetailsTimerRef.current);
      defaultTabDetailsTimerRef.current = null;
    }
    if (!registryLoadedRef.current && (bootstrapSummary?.registry_summary?.camera_count || 0) > 0) {
      setRegistryLoading(true);
    }
    defaultTabDetailsTimerRef.current = setTimeout(() => {
      fetchRegistryCameras();
      fetchRegistryPipelines();
      fetchRegistryPolicies();
    }, 600);

    const interval = setInterval(() => {
      if (!isDocumentVisible()) return;
      fetchRegistryCameras({ silent: true });
      fetchRegistryPipelines({ silent: true });
      fetchRegistryPolicies({ silent: true });
    }, 60000);

    return () => {
      if (defaultTabDetailsTimerRef.current) {
        clearTimeout(defaultTabDetailsTimerRef.current);
        defaultTabDetailsTimerRef.current = null;
      }
      clearInterval(interval);
    };
  }, [
    device?.device_id,
    tabValue,
    bootstrapSummary?.registry_summary?.camera_count,
    fetchRegistryCameras,
    fetchRegistryPipelines,
    fetchRegistryPolicies,
  ]);

  useEffect(() => {
    if (!device?.device_id || tabValue === 0) return;

    fetchDevOtaBuildEnabled();
    fetchReleaseStatus();
    fetchDesiredRelease();
    fetchAgentStatus();
    fetchRunnerLatest();

    const interval = setInterval(() => {
      if (!isDocumentVisible()) return;
      fetchReleaseStatus();
      fetchDesiredRelease();
      fetchAgentStatus();
      fetchRunnerLatest();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [
    device?.device_id,
    tabValue,
    fetchReleaseStatus,
    fetchDesiredRelease,
    fetchDevOtaBuildEnabled,
    fetchAgentStatus,
    fetchRunnerLatest,
  ]);

  useEffect(() => {
    if (!device?.device_id || tabValue !== 1 || !showModelsPanel) return;

    fetchDesiredModels();
    fetchDesiredStateProof();
    fetchEdgePipelinesRuntime();

    const diagnosticsInterval = setInterval(() => {
      if (!isDocumentVisible()) return;
      fetchDesiredModels({ silent: true });
      fetchDesiredStateProof({ silent: true });
      fetchEdgePipelinesRuntime({ silent: true });
    }, 30000);

    return () => {
      clearInterval(diagnosticsInterval);
    };
  }, [
    device?.device_id,
    tabValue,
    showModelsPanel,
    fetchDesiredModels,
    fetchDesiredStateProof,
    fetchEdgePipelinesRuntime,
  ]);

  useEffect(() => {
    if (!device?.device_id) return;
    if (!(softwareOtaExpanded || releaseDialogOpen)) return;
    fetchReleasesCatalog();
  }, [device?.device_id, softwareOtaExpanded, releaseDialogOpen, fetchReleasesCatalog]);

  useEffect(() => {
    if (!device?.device_id || tabValue !== 2) return;

    fetchIncidentHealth();

    // Incident E2E health fans into a deeper edge diagnostic path than the
    // top-bar metrics loop. Keep it fresh enough for operators, but avoid
    // hitting the edge every 30s on constrained devices when the tab is left open.
    const incidentInterval = setInterval(() => {
      if (!isDocumentVisible()) return;
      fetchIncidentHealth();
    }, 60000);

    return () => {
      clearInterval(incidentInterval);
    };
  }, [
    device?.device_id,
    tabValue,
    fetchIncidentHealth,
  ]);

  useEffect(() => {
    return () => {
      if (otaPollIntervalRef.current) {
        clearInterval(otaPollIntervalRef.current);
        otaPollIntervalRef.current = null;
      }
      if (devOtaBuildPollRef.current) {
        clearInterval(devOtaBuildPollRef.current);
        devOtaBuildPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!runnerDialogOpen || !device?.device_id) return;

    if (runnerDialogRunId) {
      void fetchRunnerRun(runnerDialogRunId);
      void fetchRunnerLogs(runnerDialogRunId);
    } else if (runnerLatest?.run_id) {
      setRunnerDialogRunId(String(runnerLatest.run_id));
      void fetchRunnerRun(runnerLatest.run_id);
      void fetchRunnerLogs(runnerLatest.run_id);
    } else {
      void fetchRunnerLatest();
    }

    const runnerInterval = setInterval(() => {
      if (runnerDialogRunId) {
        void fetchRunnerRun(runnerDialogRunId);
        void fetchRunnerLogs(runnerDialogRunId);
      } else if (runnerLatest?.run_id) {
        void fetchRunnerRun(runnerLatest.run_id);
        void fetchRunnerLogs(runnerLatest.run_id);
      } else {
        void fetchRunnerLatest();
      }
    }, 3000);

    return () => clearInterval(runnerInterval);
  }, [
    runnerDialogOpen,
    runnerDialogRunId,
    device?.device_id,
    runnerLatest?.run_id,
    runnerLatest?.status,
    fetchRunnerLatest,
    fetchRunnerRun,
    fetchRunnerLogs,
  ]);

  const handleTestFeed = async (cameraId: string) => {
    if (!device?.device_id) return;
    try {
      setRegistryLoading(true);
      const res = await api.post(
        `/api/v1/registry/devices/${device.device_id}/cameras/${cameraId}/test_feed`,
        { force_probe: true, timeout_sec: 15, decode_seconds: 5 },
      );
      const updated = res.data?.camera;
      const edgeOk = res.data?.edge_result?.ok === true;
      const edgeErr =
        res.data?.edge_result?.last_error ||
        res.data?.edge_result?.error ||
        (typeof res.data?.edge_result?.detail === 'string' ? res.data?.edge_result?.detail : null) ||
        updated?.health?.last_error ||
        'unknown';
      if (updated) {
        setRegistryCameras((prev) => prev.map((c) => (c.camera_id === cameraId ? updated : c)));
      } else {
        fetchRegistryCameras();
      }
      if (edgeOk) {
        setSnackbar({ open: true, message: 'Test Feed OK', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: `Test Feed failed: ${edgeErr}`, severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Test Feed failed', severity: 'error' });
    } finally {
      setRegistryLoading(false);
    }
  };

  const handleStopCameraPipeline = async (cameraId: string) => {
    if (!device?.device_id) return;
    setCameraOpLoading((prev) => ({ ...prev, [cameraId]: true }));
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/pipelines/cameras/${cameraId}/stop`, {
        reason: 'dashboard_device_control',
        // IMPORTANT: Always send a TTL unless user explicitly chooses indefinite.
        // The edge supports ttl_sec (see edge health_api: /control/pipelines/cameras/{camera_id}/stop).
        ...(stopDialog.cameraId === cameraId && stopDialog.ttlSec !== null ? { ttl_sec: stopDialog.ttlSec } : {}),
      });
      if (res?.data?.ok === false || res?.data?.error) {
        throw new Error(res?.data?.message || res?.data?.error || 'stop_failed');
      }
      // Refresh runtime status silently (avoid flicker)
      fetchRegistryPipelines({ silent: true });
      const ttl = stopDialog.cameraId === cameraId ? stopDialog.ttlSec : null;
      const ttlMsg = ttl === null ? ' (until you resume)' : ttl > 0 ? ` (auto-resume in ~${Math.round(ttl / 60)} min)` : '';
      const stoppedNow = res?.data?.stopped_now === true;
      const pid = res?.data?.pid;
      if (stoppedNow) {
        setSnackbar({ open: true, message: `Stopped camera pipeline: ${cameraId}${ttlMsg}`, severity: 'success' });
      } else {
        // Common case: pipeline was already dead/broken; we still set the override so the orchestrator
        // won't restart it. Make that explicit so operators don't get misled.
        const pidMsg = pid ? ` (pid=${pid})` : '';
        setSnackbar({
          open: true,
          message: `Stop override set for camera: ${cameraId}${pidMsg}${ttlMsg} (pipeline was not running)`,
          severity: 'info',
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || err?.message || `Failed to stop camera pipeline: ${cameraId}`,
        severity: 'error',
      });
    } finally {
      setCameraOpLoading((prev) => ({ ...prev, [cameraId]: false }));
    }
  };

  const handleStartCameraPipeline = async (cameraId: string) => {
    if (!device?.device_id) return;
    setCameraOpLoading((prev) => ({ ...prev, [cameraId]: true }));
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${device.device_id}/edge/pipelines/cameras/${cameraId}/start`, {});
      if (res?.data?.ok === false || res?.data?.error) {
        throw new Error(res?.data?.message || res?.data?.error || 'start_failed');
      }
      fetchRegistryPipelines({ silent: true });
      setSnackbar({ open: true, message: `Start requested for camera pipeline: ${cameraId}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.response?.data?.error || err?.message || `Failed to start camera pipeline: ${cameraId}`,
        severity: 'error',
      });
    } finally {
      setCameraOpLoading((prev) => ({ ...prev, [cameraId]: false }));
    }
  };

  const _normalizeDisplayMode = (raw: any): CameraDisplayMode => {
    const m = String(raw || '').trim().toLowerCase();
    if (m === 'headless') return 'headless';
    if (m === 'wayland') return 'wayland';
    // Back-compat: some earlier edge builds used "none" to mean headless.
    if (m === 'none') return 'headless';
    if (m === 'auto') return 'auto';
    return 'auto';
  };

  const handleSetCameraDisplayMode = async (cameraId: string, mode: CameraDisplayMode) => {
    if (!device?.device_id) return;
    setCameraOpLoading((prev) => ({ ...prev, [cameraId]: true }));
    try {
      const res = await api.post(
        `/api/v1/pipeline/devices/${device.device_id}/edge/pipelines/cameras/${cameraId}/display_mode`,
        {
          mode,
          restart: true,
          // Safety default: avoid permanently pinning an operator override unless we add an explicit
          // "persist until changed" UI. 1 hour is enough for investigation/testing.
          ttl_sec: 60 * 60,
          reason: 'dashboard_device_control',
        }
      );
      if (res?.data?.ok === false || res?.data?.error) {
        throw new Error(res?.data?.message || res?.data?.error || 'display_mode_failed');
      }
      fetchRegistryPipelines({ silent: true });
      setSnackbar({ open: true, message: `Display mode set: ${cameraId} → ${mode} (1h)`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          `Failed to set display mode for camera: ${cameraId}`,
        severity: 'error',
      });
    } finally {
      setCameraOpLoading((prev) => ({ ...prev, [cameraId]: false }));
    }
  };

  const stopTtlOptions: StopTtlOption[] = [
    { label: '15 minutes', ttlSec: 15 * 60, helper: 'Good for quick maintenance' },
    { label: '1 hour (recommended)', ttlSec: 60 * 60, helper: 'Prevents overnight surprises' },
    { label: '4 hours', ttlSec: 4 * 60 * 60 },
    { label: 'Until I resume (indefinite)', ttlSec: null, helper: 'Pipelines will stay stopped until you click Resume' },
  ];

  const openStopDialog = (cameraId: string) => {
    setStopDialog({ open: true, cameraId, ttlSec: 60 * 60 });
  };

  const confirmStopDialog = async () => {
    const cameraId = stopDialog.cameraId;
    if (!cameraId) return;
    setStopDialog((prev) => ({ ...prev, open: false }));
    await handleStopCameraPipeline(cameraId);
  };

  // Log streaming
  useEffect(() => {
    if (logsEnabled) {
      fetchLogs();
      logsIntervalRef.current = setInterval(fetchLogs, 2000);
    } else {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
        logsIntervalRef.current = null;
      }
    }

    return () => {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
      }
    };
  }, [logsEnabled, fetchLogs]);

  // Initialize connection settings
  useEffect(() => {
    if (device) {
      setConnectionSettings({
        ip_address: device.ip_address || '',
        health_api_port: device.device_metadata?.health_api_port || 8080,
      });
      const desiredState = device.device_metadata?.desired_state || {};
      const reviewRing = desiredState.review_ring || {};
      const cameraIds = Array.isArray(reviewRing.camera_ids)
        ? reviewRing.camera_ids
        : typeof reviewRing.camera_ids === 'string'
        ? reviewRing.camera_ids.split(',')
        : [];
      setReviewRingSettings({
        enabled: reviewRing.enabled !== false,
        camera_ids: cameraIds.map((value: unknown) => String(value || '').trim()).filter(Boolean).join(','),
      });
      setReviewRingSettingsChanged(false);
    }
  }, [device]);

  // Pipeline/Camera control functions
  // (pipeline control actions removed; use Setup Wizard for deploy/verify)

  // Save connection settings
  const handleSaveConnectionSettings = async () => {
    if (!deviceId) return;

    try {
      await devicesApi.updateDevice(deviceId, {
        device_metadata: {
          ...device?.device_metadata,
          health_api_port: connectionSettings.health_api_port,
        },
      });
      setSnackbar({ open: true, message: 'Connection settings saved', severity: 'success' });
      setConnectionSettingsChanged(false);
      fetchDevice();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to save settings', severity: 'error' });
    }
  };

  const handleSaveReviewRingSettings = async () => {
    if (!deviceId) return;

    try {
      const desiredState = (device?.device_metadata?.desired_state || {}) as Record<string, any>;
      const nextCameraIds = reviewRingSettings.camera_ids
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      await devicesApi.updateDevice(deviceId, {
        device_metadata: {
          ...device?.device_metadata,
          desired_state: {
            ...desiredState,
            review_ring: {
              enabled: reviewRingSettings.enabled,
              camera_ids: nextCameraIds,
            },
          },
        },
      });
      setSnackbar({
        open: true,
        message: 'Desired review ring settings saved',
        severity: 'success',
      });
      setReviewRingSettingsChanged(false);
      fetchDevice();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to save desired review ring settings',
        severity: 'error',
      });
    }
  };

  // Helpers
  const getStatusColor = (state: string) => {
    switch (state) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'error';
      case 'starting':
      case 'stopping':
      case 'switching':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getRtspOutputUrl = (cameraId: string, runtime?: any) => {
    const ip = device?.ip_address || 'localhost';
    // Mount point based on camera ID: /hardware_cam, /rtsp_cam_1, etc.
    const mpoint = cameraId.replace(/[^a-zA-Z0-9_]/g, '_');
    const runtimePort = Number(runtime?.rtsp_output_port);
    const port =
      Number.isFinite(runtimePort) && runtimePort > 0
        ? runtimePort
        : cameraId.startsWith('cam_')
          ? 8901
          : 8900;
    return `rtsp://${ip}:${port}/${mpoint}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
  };

  const fetchAuditTail = useCallback(async () => {
    if (!pipelineDeviceId) return;
    setAuditTailLoading(true);
    try {
      const res = await api.get(`/api/v1/audit/device/${pipelineDeviceId}`, { params: { limit: 50 } });
      const items = Array.isArray(res?.data?.history) ? res.data.history : [];
      setAuditTail(items);
    } catch (e: any) {
      // Non-fatal: audit requires CAP_AUDIT_READ
      setAuditTail([]);
    } finally {
      setAuditTailLoading(false);
    }
  }, [pipelineDeviceId]);

  useEffect(() => {
    if (!pipelineDeviceId) return;
    if (auditTailTimerRef.current) {
      clearTimeout(auditTailTimerRef.current);
      auditTailTimerRef.current = null;
    }
    auditTailTimerRef.current = setTimeout(() => {
      if (!isDocumentVisible()) return;
      fetchAuditTail();
    }, 1500);
    return () => {
      if (auditTailTimerRef.current) {
        clearTimeout(auditTailTimerRef.current);
        auditTailTimerRef.current = null;
      }
    };
  }, [pipelineDeviceId, fetchAuditTail]);

  const runEdgePreflight = useCallback(async () => {
    if (!pipelineDeviceId) return;
    setEdgePreflightRunning(true);
    setEdgePreflightResult(null);
    try {
      const res = await api.post(`/api/v1/preflight/devices/${pipelineDeviceId}/edge_run`, {
        timeout_sec: 120,
        cameras: false,
      });
      setEdgePreflightResult(res?.data || null);
      setSnackbar({ open: true, message: (res?.data?.overall ? `Edge preflight: ${res.data.overall}` : 'Edge preflight completed'), severity: res?.data?.overall === 'FAIL' ? 'error' : res?.data?.overall === 'WARN' ? 'info' : 'success' });
      fetchAuditTail();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Edge preflight failed', severity: 'error' });
    } finally {
      setEdgePreflightRunning(false);
    }
  }, [pipelineDeviceId, fetchAuditTail]);

  const downloadAuthedZip = async (url: string, filename: string) => {
    const res = await api.get(url, { responseType: 'blob' as any });
    const blob = new Blob([res.data], { type: 'application/zip' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(a.href);
  };

  const startDiagnosticBundle = useCallback(async () => {
    if (!pipelineDeviceId) return;
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${pipelineDeviceId}/diagnostic_bundle`, {});
      const jobId = String(res?.data?.job_id || '');
      if (!jobId) throw new Error('job_id missing');
      setDiagJobId(jobId);
      setDiagJobStatus({ status: 'running' });
      setSnackbar({ open: true, message: 'Diagnostic bundle started', severity: 'info' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Diagnostic bundle start failed', severity: 'error' });
    }
  }, [pipelineDeviceId]);

  useEffect(() => {
    if (!pipelineDeviceId || !diagJobId) return;
    if (diagPollRef.current) clearInterval(diagPollRef.current);
    diagPollRef.current = setInterval(async () => {
      try {
        const st = await api.get(`/api/v1/pipeline/devices/${pipelineDeviceId}/diagnostic_bundle/${diagJobId}`);
        setDiagJobStatus(st?.data || null);
        const status = String(st?.data?.status || '');
        if (status === 'done') {
          clearInterval(diagPollRef.current);
          diagPollRef.current = null;
          const dlUrl = `/api/v1/pipeline/devices/${pipelineDeviceId}/diagnostic_bundle/${diagJobId}/download`;
          await downloadAuthedZip(dlUrl, `diagnostic_bundle_${pipelineDeviceId}_${diagJobId}.zip`);
          setSnackbar({ open: true, message: 'Diagnostic bundle downloaded', severity: 'success' });
        }
        if (status === 'error') {
          clearInterval(diagPollRef.current);
          diagPollRef.current = null;
          setSnackbar({ open: true, message: st?.data?.error || 'Diagnostic bundle failed', severity: 'error' });
        }
      } catch (e: any) {
        // stop polling on repeated failures
      }
    }, 1500);
    return () => {
      if (diagPollRef.current) clearInterval(diagPollRef.current);
      diagPollRef.current = null;
    };
  }, [pipelineDeviceId, diagJobId]);

  if (error && !device) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/devices')} sx={{ mt: 2 }}>
          Back to Devices
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/devices')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Computer sx={{ fontSize: 40, color: 'primary.main' }} />
            {device ? device.name : <Skeleton variant="text" width={220} />}
            {device ? (
              <Tooltip title={device.lifecycle_summary?.reason || getDeviceLifecycleMetadata(device.lifecycle_summary?.state).reason} arrow>
                <Chip
                  size="small"
                  label={getDeviceLifecycleMetadata(device.lifecycle_summary?.state).label}
                  color={getDeviceLifecycleMetadata(device.lifecycle_summary?.state).color}
                />
              </Tooltip>
            ) : null}
            {device ? (
              <Chip
                size="small"
                label={pipelineStatus?.state || 'unknown'}
                color={getStatusColor(pipelineStatus?.state || 'unknown') as any}
                icon={pipelineStatus?.state === 'running' ? <FiberManualRecord sx={{ fontSize: 12 }} /> : undefined}
              />
            ) : (
              <Skeleton variant="rounded" width={80} height={24} />
            )}
          </Typography>
          {device ? (
            <Typography variant="body2" color="text.secondary">
              {device.device_id} • {device.ip_address || 'IP not configured'}
            </Typography>
          ) : (
            <Skeleton variant="text" width={300} />
          )}
          {latestIncidentSummary ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`Latest incident: ${latestIncidentLabel}${latestIncidentTimestampLabel ? ` | ${latestIncidentTimestampLabel}` : ''}`}
              />
              {latestIncidentEvidenceState ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color={latestIncidentEvidenceState.toLowerCase() === 'ready' ? 'success' : 'default'}
                  label={`Evidence ${latestIncidentEvidenceState.toLowerCase()}`}
                />
              ) : null}
              {latestIncidentSourceContext ? (
                <Chip size="small" variant="outlined" label={`Source ${latestIncidentSourceContext.toLowerCase()}`} />
              ) : null}
              {latestIncidentProofScopeId ? (
                <Chip size="small" variant="outlined" label={`Scope ${latestIncidentProofScopeId}`} />
              ) : null}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: { xs: 180, sm: 320 } }}>
            <InputLabel id="device-control-device-select-label">Edge unit</InputLabel>
            <Select
              labelId="device-control-device-select-label"
              value={device?.id || deviceId || ''}
              label="Edge unit"
              onOpen={() => {
                if (controlDeviceOptions.length === 0) void loadControlDeviceOptions();
              }}
              onChange={(e) => handleSelectControlDevice(String(e.target.value))}
            >
              {device?.id &&
                !controlDeviceOptions.some((item) => item.id === device.id) && (
                  <MenuItem value={device.id}>
                    {device.name || device.device_id} ({device.device_id})
                  </MenuItem>
                )}
              {controlDeviceOptions.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name || item.device_id} ({item.device_id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {grafanaPilotOverviewUrl && (
            <Tooltip title="View in Grafana (Pilot Overview)">
              <IconButton component="a" href={grafanaPilotOverviewUrl} target="_blank" rel="noreferrer">
                <LinkIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => {
                fetchBootstrapSummary();
                fetchAuditTail();
                if (tabValue === 0) {
                  fetchRegistryCameras();
                  fetchRegistryPipelines();
                  fetchRegistryPolicies();
                }
                if (tabValue !== 0) {
                  fetchRunnerLatest();
                  fetchReleaseStatus();
                  fetchDesiredRelease();
                  fetchAgentStatus();
                }
                if (tabValue === 1 && showModelsPanel) {
                  fetchDesiredModels();
                  fetchDesiredStateProof();
                  fetchEdgePipelinesRuntime();
                }
                if (softwareOtaExpanded || releaseDialogOpen) {
                  fetchReleasesCatalog();
                }
                if (tabValue === 2) {
                  fetchIncidentHealth();
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Firmware + Preflight readiness (edge + image build) */}
      {device?.device_id && (
        <Box sx={{ mb: 3 }}>
          <FirmwareReadinessPanel deviceId={device.device_id} />
        </Box>
      )}

      {device?.device_id && (
        <Box sx={{ mb: 3 }}>
          <EnterpriseProductionDeployPanel deviceId={device.device_id} device={device} />
        </Box>
      )}

      {/* Control Center (single-pane quick actions + recent audit tail) */}
      {pipelineDeviceId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Typography variant="h6">Control Center</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button size="small" variant="outlined" onClick={runEdgePreflight} disabled={edgePreflightRunning}>
                {edgePreflightRunning ? 'Running preflight…' : 'Run Edge Preflight'}
              </Button>
              <Button size="small" variant="outlined" onClick={startDiagnosticBundle} disabled={Boolean(diagJobId) && String(diagJobStatus?.status || '').toLowerCase() === 'running'}>
                Diagnostic Bundle
              </Button>
              <Button size="small" variant="text" onClick={fetchAuditTail} disabled={auditTailLoading}>
                Refresh actions
              </Button>
            </Box>
          </Box>

          {edgePreflightResult?.overall && (
            <Alert severity={edgePreflightResult.overall === 'FAIL' ? 'error' : edgePreflightResult.overall === 'WARN' ? 'warning' : 'success'} sx={{ mb: 1 }}>
              <AlertTitle>Edge preflight: {edgePreflightResult.overall}</AlertTitle>
              {Array.isArray(edgePreflightResult?.hints) && edgePreflightResult.hints.length > 0 ? edgePreflightResult.hints[0] : 'See report for details.'}
            </Alert>
          )}

          {diagJobId && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Diagnostic bundle job: <b>{diagJobId}</b> • status: <b>{String(diagJobStatus?.status || 'running')}</b>
              </Typography>
              {String(diagJobStatus?.status || '').toLowerCase() === 'running' && <LinearProgress sx={{ mt: 0.5 }} />}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent actions
          </Typography>
          {auditTailLoading ? (
            <Box>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width={`${70 - i * 10}%`} />
                </Box>
              ))}
            </Box>
          ) : auditTail.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No recent actions (or you don’t have audit permissions).
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
              {auditTail.slice(0, 30).map((it) => (
                <ListItem key={it.id} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {it.success === false ? <ErrorIcon fontSize="small" color="error" /> : <CheckCircle fontSize="small" color="success" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${it.action} • ${it.resource_type}`}
                    secondary={`${it.timestamp} • ${it.resource_id}${it.username ? ` • ${it.username}` : ''}${it.error_message ? ` • ${it.error_message}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Quick Metrics Bar */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                <Typography variant="h4" color="primary.main">
                  {metrics.fps.toFixed(1)}
                </Typography>
              ) : (
                <Skeleton variant="text" width={60} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Speed sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                FPS
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                <Typography variant="h4" color={metrics.latency_ms > 100 ? 'warning.main' : 'success.main'}>
                  {metrics.latency_ms.toFixed(0)}ms
                </Typography>
              ) : (
                <Skeleton variant="text" width={70} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Timer sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Latency
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                <Typography variant="h4" color={metrics.cpu_percent > 80 ? 'error.main' : 'text.primary'}>
                  {metrics.cpu_percent.toFixed(0)}%
                </Typography>
              ) : (
                <Skeleton variant="text" width={50} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Memory sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                CPU
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                <Typography variant="h4" color={metrics.memory_percent > 80 ? 'error.main' : 'text.primary'}>
                  {metrics.memory_percent.toFixed(0)}%
                </Typography>
              ) : (
                <Skeleton variant="text" width={50} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Storage sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Memory
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                (() => {
                  const t = metrics.temperature_c;
                  const tc = typeof t === 'number' && Number.isFinite(t) ? t : null;
                  const color =
                    tc === null
                      ? 'text.primary'
                      : tc >= 90
                        ? 'error.main'
                        : tc >= 80
                          ? 'warning.main'
                          : tc >= 70
                            ? 'warning.main'
                            : 'success.main';
                  const label = tc === null ? '--' : `${tc.toFixed(1)}°C`;
                  return (
                    <Typography variant="h4" color={color}>
                      {label}
                    </Typography>
                  );
                })()
              ) : (
                <Skeleton variant="text" width={70} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <DeviceThermostat sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Temp
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded ? (
                <Typography variant="h4" color="secondary.main">
                  {metrics.detections_per_second.toFixed(1)}
                </Typography>
              ) : (
                <Skeleton variant="text" width={50} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Det/sec
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Box sx={{ textAlign: 'center' }}>
              {metricsLoaded || pipelineStatus ? (
                <Typography variant="h4">
                  {pipelineStatus?.uptime_seconds ? formatUptime(pipelineStatus.uptime_seconds) : '--'}
                </Typography>
              ) : (
                <Skeleton variant="text" width={70} height={42} sx={{ mx: 'auto' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                <Timeline sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Uptime
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs - Simplified to 3 */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<Videocam />} label="Cameras" />
          <Tab icon={<Settings />} label="Connection" />
          <Tab icon={<Terminal />} label="Diagnostics" />
        </Tabs>

        {/* ==================== CAMERAS TAB ==================== */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3, pb: 3 }}>
            {(() => {
              const stopped = Object.entries(registryPipelines || {}).filter(
                ([, v]) => String(v?.runtime_desired_state || '').toLowerCase() === 'stopped'
              );
              if (stopped.length === 0) return null;
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Manual stop override active</AlertTitle>
                  One or more camera pipelines are stopped by a runtime override. This persists until you click “Resume” (or until TTL expiry if configured).
                </Alert>
              );
            })()}
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/setup?device=${encodeURIComponent(device?.device_id || '')}`)}
                >
                  Open Setup Wizard
                </Button>
              }
            >
              <AlertTitle>Configuration lives in Setup Wizard</AlertTitle>
              Cameras (source/credentials) and Policies (PPE, zone intrusion, dwell, loitering) are configured in the Setup Wizard.
              This page is for runtime status and diagnostics.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CameraAlt color="primary" />
              Camera Sources
              <Chip
                label={`${
                  registryLoadedRef.current
                    ? registryCameras.length
                    : (bootstrapSummary?.registry_summary?.camera_count ?? registryCameras.length)
                } camera(s)`}
                size="small"
              />
            </Typography>

            {/* Registry cameras (Phase 1) */}
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title="Configured Cameras (Registry)"
                subheader="Read-only view of cameras attached in the cloud registry. Manage cameras in the Setup Wizard."
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/setup?device=${encodeURIComponent(device?.device_id || '')}`)}
                  >
                    Manage in Wizard
                  </Button>
                }
              />
              <CardContent>
                {registryLoading && registryCameras.length === 0 ? (
                  <Box>
                    {[0, 1].map((i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="40%" />
                        </Box>
                        <Skeleton variant="rounded" width={80} height={30} />
                      </Box>
                    ))}
                  </Box>
                ) : registryCameras.length === 0 ? (
                  <Alert severity="info">No registry cameras yet. Add cameras in the Setup Wizard.</Alert>
                ) : (
                  <List dense>
                    {registryCameras.map((c) => {
                      const health = c.health || {};
                      const status = health.status || 'unknown';
                      const last = health.last_test_at ? new Date(health.last_test_at).toLocaleString() : 'never';
                      const err = health.last_error;
                      return (
                        <ListItem
                          key={c.camera_id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleTestFeed(c.camera_id)}
                                disabled={registryLoading}
                              >
                                Test Feed
                              </Button>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={`${c.display_name || c.camera_id} (${c.camera_id})`}
                            secondary={`status=${status} • last_test=${last}${err ? ` • error=${err}` : ''}`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>

            {registryCameras.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No registry cameras yet. Add cameras in the Setup Wizard.
              </Alert>
            ) : (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {registryCameras.map((camera) => {
                  const srcType = String((camera as any)?.source?.type || '').toLowerCase();
                  const camType: 'hardware' | 'rtsp' = srcType === 'rtsp' ? 'rtsp' : 'hardware';

                  const cameraId = camera.camera_id;
                  const displayName = camera.display_name || cameraId;

                  const runtime = registryPipelines?.[cameraId];
                  const aliveValue = runtime?.alive;
                  const aliveKnown = typeof aliveValue === 'boolean';
                  const alive = aliveValue === true;
                  const stoppedByOverride =
                    String(runtime?.runtime_desired_state || '').toLowerCase() === 'stopped';
                  const currentDisplayMode: CameraDisplayMode = _normalizeDisplayMode(
                    (runtime?.runtime_override || {})?.pipeline_display_mode
                  );
                  const srcReachable = runtime?.source_reachable;
                  const srcError = runtime?.source_error;
                  const lastErrorLine = (runtime as any)?.last_error_line as string | null | undefined;
                  const logTail = (runtime as any)?.log_tail as string[] | null | undefined;
                  const logTailTruncated = (runtime as any)?.log_tail_truncated as boolean | null | undefined;

                  const perCamPipeline = runtime?.pipeline;
                  const pipelineName = perCamPipeline || pipelineStatus?.pipeline_name || '';
                  const detectionEnabled = true;
                  const poseEnabled = pipelineName.includes('pose');
                  const ppeEnabled = pipelineName.includes('ppe');

                  const rtspInput: string | null =
                    camType === 'rtsp' ? (String((camera as any)?.source?.uri || '').trim() || null) : null;

                  return (
                  <Grid item xs={12} md={6} key={cameraId}>
                    <Card
                      sx={{
                        border: 2,
                        borderColor: alive ? 'success.300' : 'grey.300',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: 4 },
                      }}
                    >
                      <CardHeader
                        avatar={
                          camType === 'hardware' ? (
                            <CameraAlt sx={{ fontSize: 32, color: 'success.main' }} />
                          ) : (
                            <Videocam sx={{ fontSize: 32, color: 'info.main' }} />
                          )
                        }
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{displayName}</Typography>
                            <Chip
                              label={!aliveKnown ? 'unknown' : alive ? 'active' : 'inactive'}
                              size="small"
                              color={alive ? 'success' : 'default'}
                              variant={!aliveKnown ? 'outlined' : 'filled'}
                              icon={alive ? <FiberManualRecord sx={{ fontSize: 10 }} /> : undefined}
                            />
                          </Box>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            {camType === 'hardware' ? 'Hardware Camera' : 'RTSP Stream'} • {cameraId}
                          </Typography>
                        }
                      />

                      <CardContent sx={{ pt: 0 }}>
                        {/* Pipeline modules (runtime graph, NOT policies) */}
                        <Tooltip
                          arrow
                          title="These indicate which CV modules are present/active in the running pipeline graph (runtime). Policies are configured separately in the Setup Wizard."
                        >
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ width: 'fit-content' }}>
                            Pipeline Modules (runtime)
                          </Typography>
                        </Tooltip>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          <FeatureChip label="Detection" enabled={detectionEnabled} color="primary" />
                          <FeatureChip label="Pose" enabled={poseEnabled} color="info" />
                          <FeatureChip label="PPE" enabled={ppeEnabled} color="warning" />
                        </Box>

                        {/* Policies (configured via Setup Wizard) */}
                        {(() => {
                          const deviceWide = registryDevicePolicies || [];
                          const perCam = registryPoliciesByCamera?.[cameraId] || [];
                          const all = [...deviceWide, ...perCam].filter((p) => p && p.enabled !== false);
                          const severityOf = (p: any) => String(p?.config?.severity || 'medium').toLowerCase();
                          const chipColor = (sev: string): 'default' | 'warning' | 'error' => {
                            if (sev === 'critical') return 'error';
                            if (sev === 'high') return 'warning';
                            return 'default';
                          };
                          return (
                            <>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                                Policies (configured)
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {all.length > 0 ? (
                                  all.slice(0, 8).map((p) => {
                                    const sev = severityOf(p);
                                    const label = p?.policy_type || 'policy';
                                    const name = p?.display_name || p?.policy_id;
                                    return (
                                      <Tooltip key={p.policy_id} title={`${name}${p.camera_id ? '' : ' (device-wide)'}`}>
                                        <Chip
                                          size="small"
                                          label={label}
                                          color={chipColor(sev)}
                                          variant={p.camera_id ? 'filled' : 'outlined'}
                                        />
                                      </Tooltip>
                                    );
                                  })
                                ) : (
                                  <Chip size="small" label="No policies" variant="outlined" />
                                )}
                              </Box>
                            </>
                          );
                        })()}

                        {/* Runtime pipeline controls (orchestrator-aware) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Runtime:
                            </Typography>
                            {!aliveKnown ? (
                              <Chip size="small" color="default" variant="outlined" label="unknown" />
                            ) : alive ? (
                              <Chip size="small" color="success" label="running" />
                            ) : (
                              <Chip size="small" color="default" variant="outlined" label="not running" />
                            )}
                            {camType === 'rtsp' && aliveKnown && (
                              srcReachable === true ? (
                                <Chip size="small" color="success" variant="outlined" label="rtsp reachable" />
                              ) : srcReachable === false ? (
                                <Tooltip title={srcError || 'RTSP host not reachable'}>
                                  <Chip size="small" color="error" variant="outlined" label="rtsp unreachable" />
                                </Tooltip>
                              ) : (
                                <Chip size="small" color="default" variant="outlined" label="rtsp unknown" />
                              )
                            )}
                            {stoppedByOverride && (
                              <Tooltip title={(runtime?.runtime_override || {})?.reason || 'Stopped by runtime override'}>
                                <Chip size="small" color="warning" label="manual stop" />
                              </Tooltip>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel id={`display-mode-label-${cameraId}`}>Display</InputLabel>
                                <Select
                                  labelId={`display-mode-label-${cameraId}`}
                                  id={`display-mode-${cameraId}`}
                                  value={currentDisplayMode}
                                  label="Display"
                                  onChange={(e) => handleSetCameraDisplayMode(cameraId, e.target.value as CameraDisplayMode)}
                                  disabled={!!cameraOpLoading[cameraId]}
                                >
                                  <MenuItem value="auto">Auto</MenuItem>
                                  <MenuItem value="wayland">Wayland</MenuItem>
                                  <MenuItem value="headless">Headless</MenuItem>
                                </Select>
                              </FormControl>
                              <Tooltip
                                arrow
                                disableInteractive
                                title="Display mode controls whether this camera uses Wayland (on-screen) or runs headless. Applies for 1 hour and restarts the pipeline."
                              >
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <InfoOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {(() => {
                              // Correct UX:
                              // - If pipeline is running, show Stop
                              // - If pipeline is not running (or manually stopped), show Start
                              if (aliveKnown && alive && !stoppedByOverride) {
                                return (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => openStopDialog(cameraId)}
                                    disabled={!!cameraOpLoading[cameraId]}
                                  >
                                    Stop
                                  </Button>
                                );
                              }

                              return (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleStartCameraPipeline(cameraId)}
                                  disabled={!!cameraOpLoading[cameraId]}
                                >
                                  {stoppedByOverride ? 'Resume' : 'Start'}
                                </Button>
                              );
                            })()}
                          </Box>
                        </Box>

                        {/* Last failure / crash details (best-effort) */}
                        {(() => {
                          const hasLogTail = Array.isArray(logTail) && logTail.length > 0;
                          const hasAny = Boolean(lastErrorLine) || hasLogTail;
                          if (!hasAny) return null;

                          // If operator manually stopped, don't show a scary crash panel (it can be stale/misleading).
                          if (stoppedByOverride) return null;

                          const isCurrentlyDown = aliveKnown && !alive;
                          const severity: 'info' | 'warning' = isCurrentlyDown ? 'warning' : 'info';
                          const title = isCurrentlyDown ? 'Pipeline not running' : 'Last failure (previous crash)';

                          return (
                            <>
                              {lastErrorLine && (
                                <Alert severity={severity} sx={{ mb: 1 }}>
                                  <AlertTitle>{title}</AlertTitle>
                                  {lastErrorLine}
                                </Alert>
                              )}
                              {hasLogTail && (
                                <Box sx={{ mb: 2 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={showCrashDetails[cameraId] ? <VisibilityOff /> : <Visibility />}
                                    onClick={() =>
                                      setShowCrashDetails((prev) => ({ ...prev, [cameraId]: !prev[cameraId] }))
                                    }
                                  >
                                    {showCrashDetails[cameraId] ? 'Hide last crash log tail' : 'Show last crash log tail'}
                                  </Button>
                                  <Collapse in={!!showCrashDetails[cameraId]}>
                                    <Paper variant="outlined" sx={{ mt: 1, p: 1, bgcolor: 'grey.50' }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Last {logTail.length} lines{logTailTruncated ? ' (truncated)' : ''}
                                      </Typography>
                                      <Box
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          mt: 0.5,
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          fontFamily: 'monospace',
                                          fontSize: 12,
                                          maxHeight: 260,
                                          overflow: 'auto',
                                        }}
                                      >
                                        {logTail.join('\n')}
                                      </Box>
                                    </Paper>
                                  </Collapse>
                                </Box>
                              )}
                            </>
                          );
                        })()}

                        <Divider sx={{ my: 2 }} />

                        {/* RTSP Output URL */}
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'primary.200',
                          }}
                        >
                          <Typography variant="caption" color="primary.main" fontWeight="bold">
                            📺 RTSP Output (Processed Stream)
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                flex: 1,
                                wordBreak: 'break-all',
                                bgcolor: 'white',
                                p: 0.5,
                                borderRadius: 0.5,
                              }}
                            >
                              {getRtspOutputUrl(cameraId, runtime)}
                            </Typography>
                            <Tooltip title="Copy URL">
                              <IconButton size="small" onClick={() => copyToClipboard(getRtspOutputUrl(cameraId, runtime))}>
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* RTSP Input (for RTSP cameras) */}
                        {camType === 'rtsp' && rtspInput && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              RTSP Input:
                            </Typography>
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}
                            >
                              {rtspInput}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )})}
              </Grid>
            )}
          </Box>
        </TabPanel>

        {/* ==================== SETTINGS TAB ==================== */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Connection settings (cloud → edge)</AlertTitle>
              These settings control how the cloud dashboard reaches the edge device (Health API port, etc).
              They do <b>not</b> configure camera policies. Use the Setup Wizard for camera sources and policies.
            </Alert>
            <Grid container spacing={3}>
              {/* Connection Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Connection Settings"
                    avatar={<NetworkCheck color="primary" />}
                    action={
                      <IconButton onClick={() => setShowConnectionSettings(!showConnectionSettings)}>
                        {showConnectionSettings ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  />
                  <Collapse in={showConnectionSettings} timeout="auto">
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Device IP Address"
                            value={connectionSettings.ip_address}
                            onChange={(e) => {
                              setConnectionSettings((prev) => ({ ...prev, ip_address: e.target.value }));
                              setConnectionSettingsChanged(true);
                            }}
                            placeholder="e.g. edge-host or IP"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="API Port"
                            type="number"
                            value={connectionSettings.health_api_port}
                            onChange={(e) => {
                              setConnectionSettings((prev) => ({
                                ...prev,
                                health_api_port: parseInt(e.target.value) || 8080,
                              }));
                              setConnectionSettingsChanged(true);
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={fetchMetrics}>
                        Test Connection
                      </Button>
                      {connectionSettingsChanged && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Save />}
                          onClick={handleSaveConnectionSettings}
                        >
                          Save
                        </Button>
                      )}
                    </CardActions>
                  </Collapse>
                  {!showConnectionSettings && (
                    <CardContent sx={{ pt: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        IP: {connectionSettings.ip_address || 'Not configured'} | Port: {connectionSettings.health_api_port}
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </Grid>

              {/* Device Info */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Desired Review Ring"
                    avatar={<Videocam color="primary" />}
                    action={
                      <IconButton onClick={() => setShowReviewRingSettings(!showReviewRingSettings)}>
                        {showReviewRingSettings ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  />
                  <Collapse in={showReviewRingSettings} timeout="auto">
                    <CardContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        This is persistent desired state. Saving here updates the cloud source of truth and the
                        next desired-state sync will carry it to the edge.
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={reviewRingSettings.enabled}
                            onChange={(_, checked) => {
                              setReviewRingSettings((prev) => ({ ...prev, enabled: checked }));
                              setReviewRingSettingsChanged(true);
                            }}
                          />
                        }
                        label="Enable review ring by default"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label="Camera allowlist (optional)"
                        placeholder="cam_1,cam_2"
                        value={reviewRingSettings.camera_ids}
                        onChange={(e) => {
                          setReviewRingSettings((prev) => ({ ...prev, camera_ids: e.target.value }));
                          setReviewRingSettingsChanged(true);
                        }}
                        helperText="Leave blank to allow all cameras. Comma-separated camera IDs."
                      />
                    </CardContent>
                    <CardActions>
                      {reviewRingSettingsChanged && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Save />}
                          onClick={handleSaveReviewRingSettings}
                        >
                          Save
                        </Button>
                      )}
                    </CardActions>
                  </Collapse>
                  {!showReviewRingSettings && (
                    <CardContent sx={{ pt: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        Enabled: {reviewRingSettings.enabled ? 'Yes' : 'No'} | Cameras:{' '}
                        {reviewRingSettings.camera_ids || 'All'}
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </Grid>

              {/* Device Info */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Device Information" avatar={<Computer color="primary" />} />
                  <CardContent>
                    {device ? (
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Device ID" secondary={device.device_id || 'Unknown'} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Name" secondary={device.name || 'Unknown'} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="MAC Address" secondary={device.mac_address || 'Unknown'} />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Last Seen"
                            secondary={
                              pipelineStatus?.last_seen
                                ? new Date(pipelineStatus.last_seen).toLocaleString()
                                : 'Unknown'
                            }
                          />
                        </ListItem>
                      </List>
                    ) : (
                      <Box>
                        {[0, 1, 2, 3].map((i) => (
                          <Box key={i} sx={{ py: 0.5 }}>
                            <Skeleton variant="text" width={100} height={16} />
                            <Skeleton variant="text" width={`${60 + i * 5}%`} height={20} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Software (collapsed) */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Software" avatar={<DirectionsRun color="secondary" />} />
                  <CardContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Update now (bundle), set desired release (OTA), or rollback — all without SSH.
                    </Typography>

                    <Accordion expanded={softwareBundleExpanded} onChange={(_, expanded) => setSoftwareBundleExpanded(expanded)}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                          <Typography variant="subtitle2">Update now (Bundle)</Typography>
                          {releaseLoading && <CircularProgress size={16} />}
                          <Chip size="small" label={(releaseStatus?.status?.state) || 'unknown'} />
                          {releaseStatus?.current?.release_id && (
                            <Chip size="small" variant="outlined" label={`current: ${releaseStatus.current.release_id}`} />
                          )}
                          {releaseStatus?.kgr?.release_id && (
                            <Chip size="small" variant="outlined" label={`kgr: ${releaseStatus.kgr.release_id}`} />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Stage, activate, and verify release bundles (A-56).
                        </Typography>
                        {releaseStatus?.status?.detail?.error && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            {releaseStatus.status.detail.error}
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Button size="small" onClick={fetchReleaseStatus} disabled={releaseLoading}>
                            Refresh
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setReleaseDialogOpen(true)}
                            disabled={runnerLoading || releaseLoading}
                          >
                            Update now…
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => runRunnerMode('verify_release')} disabled={runnerLoading}>
                            Verify
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={softwareOtaExpanded} onChange={(_, expanded) => setSoftwareOtaExpanded(expanded)}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                          <Typography variant="subtitle2">Set desired release (OTA)</Typography>
                          {(desiredReleaseLoading || agentStatusLoading || releasesCatalogLoading || otaPolling) && <CircularProgress size={16} />}
                          <Chip size="small" label={`desired: ${desiredReleaseInfo?.desired_release_id || 'none'}`} variant="outlined" />
                          <Chip
                            size="small"
                            label={`agent: ${String(agentStatus?.status || agentStatus?.error || 'unknown')}`}
                            color={String(agentStatus?.status || '').toLowerCase() === 'ok' ? 'success' : 'default'}
                            variant="outlined"
                          />
                          {desiredReleaseInfo?.desired_release?.image_digest_ref && (
                            <Chip size="small" label="digest set" color="info" variant="outlined" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Set a desired release in the cloud and let the edge pull+activate it.
                        </Typography>
                        <PlaybookCallout
                          title="Safe update / rollback sequence"
                          severity="warning"
                          compact
                          summary="Precheck before update. Use rollback only for failed release attempts, not config drift."
                          doItems={[
                            'Run precheck or Prepare for OTA before applying a desired release.',
                            'Use Rollback to known-good only after the latest release attempt is failing or support directs it.',
                          ]}
                          dontItems={[
                            'Do not switch desired release blindly while the edge is already APPLYING or STABILIZING.',
                            'Do not use rollback to fix config drift; use desired-state sync first.',
                          ]}
                          href={buildDocsUrl('training/EPIC12_WAREHOUSE_OPERATOR_TRAINING.md', 'rollback-vs-retry')}
                          linkLabel="Open operator playbook"
                          sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {otaPrecheckLoading && <CircularProgress size={16} />}
                          <Button size="small" variant="outlined" onClick={() => runOtaPrecheck()} disabled={otaPrecheckLoading || otaPolling}>
                            Run precheck
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => runOtaPrecheck({ autoFix: true })}
                            disabled={otaPrecheckLoading || otaPolling}
                          >
                            Auto-fix & recheck
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => runOtaPrecheck({ autoFix: true, pullTimeoutSec: 180 })}
                            disabled={otaPrecheckLoading || otaPolling}
                          >
                            Prepare for OTA
                          </Button>
                          {otaPrecheckResult && (
                            <Chip
                              size="small"
                              label={otaPrecheckResult?.ok ? 'precheck: PASS' : 'precheck: FAIL'}
                              color={otaPrecheckResult?.ok ? 'success' : 'error'}
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Desired release_id"
                          value={desiredReleaseId}
                          onChange={(e) => {
                            setDesiredReleaseId(e.target.value);
                            setDesiredReleaseDirty(true);
                          }}
                          helperText={
                            desiredReleaseInfo?.desired_release?.image_digest_ref
                              ? `image: ${String(desiredReleaseInfo.desired_release.image_digest_ref).slice(0, 90)}`
                              : 'Select a published release (or choose none to disable OTA).'
                          }
                          sx={{ mb: 1 }}
                        >
                          <MenuItem value="">
                            <em>(none)</em>
                          </MenuItem>
                          {desiredReleaseId &&
                            !releasesCatalog.some((r: any) => String(r.release_id) === String(desiredReleaseId)) && (
                              <MenuItem value={String(desiredReleaseId)}>{String(desiredReleaseId)} (selected)</MenuItem>
                            )}
                          {releasesCatalog.map((r: any) => (
                            <MenuItem key={String(r.release_id)} value={String(r.release_id)}>
                              {String(r.release_id)}
                            </MenuItem>
                          ))}
                        </TextField>

                        {agentStatus?.detail && (
                          <Alert severity={agentStatus?.ok === false ? 'warning' : 'info'} sx={{ mt: 1 }}>
                            {String(agentStatus.detail)}
                          </Alert>
                        )}

                        {(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state) && (
                          <Alert
                            sx={{ mt: 1 }}
                            severity={
                              String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || '')
                                .toUpperCase()
                                .includes('RUNNING')
                                ? 'success'
                                : String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || '')
                                    .toUpperCase()
                                    .includes('FAILED')
                                ? 'error'
                                : 'info'
                            }
                          >
                            Last attempt:{' '}
                            <strong>{String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || 'unknown')}</strong>
                            {releaseStatus?.last_attempt?.attempt_id ? ` • attempt=${String(releaseStatus.last_attempt.attempt_id)}` : ''}
                            {releaseStatus?.last_attempt?.error ? ` — ${String(releaseStatus.last_attempt.error)}` : ''}
                          </Alert>
                        )}

                        {Array.isArray((releaseStatus as any)?.last_attempt?.warnings) && (releaseStatus as any).last_attempt.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            Release applied with warnings ({(releaseStatus as any).last_attempt.warnings.length}). See Diagnostics → Release journal for details.
                          </Alert>
                        )}

                        <Box sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={fetchReleaseAttempts} disabled={releaseAttemptsLoading}>
                            {releaseAttemptsLoading ? 'Loading attempts…' : 'Attempt history'}
                          </Button>
                        </Box>
                        {releaseAttemptsOpen && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Recent attempts (cloud)
                            </Typography>
                            {releaseAttempts.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No attempts recorded yet.
                              </Typography>
                            ) : (
                              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                                {releaseAttempts.slice(0, 8).map((a: any) => (
                                  <li key={String(a?.attempt_id || Math.random())}>
                                    <Typography variant="body2">
                                      <strong>{String(a?.attempt_id || 'attempt')}</strong>
                                      {a?.desired_release_id ? ` • desired=${String(a.desired_release_id)}` : ''}
                                      {a?.terminal_state ? ` • terminal=${String(a.terminal_state)}` : ''}
                                      {Array.isArray(a?.warnings) && a.warnings.length ? ` • warnings=${String(a.warnings.length)}` : ''}
                                      {a?.error_code ? ` • error=${String(a.error_code)}` : ''}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Button size="small" onClick={fetchDesiredRelease} disabled={desiredReleaseLoading}>
                            Refresh desired
                          </Button>
                          <Button size="small" onClick={fetchAgentStatus} disabled={agentStatusLoading}>
                            Refresh agent
                          </Button>
                          <Button size="small" onClick={fetchReleasesCatalog} disabled={releasesCatalogLoading}>
                            Refresh releases
                          </Button>
                          <Button size="small" variant="outlined" onClick={setDesiredRelease} disabled={desiredReleaseLoading}>
                            Save desired
                          </Button>
                          <Button size="small" variant="contained" onClick={triggerAgentPollNow} disabled={agentStatusLoading || otaPolling}>
                            Poll now
                          </Button>
                          <Button size="small" variant="contained" color="success" onClick={verifyOtaUpdate} disabled={otaVerifyLoading}>
                            {otaVerifyLoading ? 'Verifying…' : 'Verify update'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={runRollbackToKnownGood}
                            disabled={desiredReleaseLoading || agentStatusLoading || otaPolling}
                          >
                            Rollback to known-good
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={runCompleteUpdate}
                            disabled={desiredReleaseLoading || agentStatusLoading || otaPolling}
                          >
                            Apply desired release
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>

              {/* Models (desired vs applied) */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Models"
                    avatar={<Memory color="info" />}
                    action={
                      <IconButton onClick={() => setShowModelsPanel((prev) => !prev)}>
                        {showModelsPanel ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                    subheader={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {modelsInSync ? (
                          <Chip
                            size="small"
                            icon={<CheckCircle fontSize="small" />}
                            label="In sync"
                            color="success"
                            variant="outlined"
                          />
                        ) : !desiredModels?.desired_model_set_id ? (
                          <Chip size="small" label="No desired set" variant="outlined" />
                        ) : (
                          <Chip
                            size="small"
                            icon={<Sync />}
                            label="Syncing..."
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                  />
                  <Collapse in={showModelsPanel} timeout="auto" unmountOnExit>
                  <CardContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Desired models are managed in the cloud; the edge downloads and verifies them. Apply changes to sync.
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      {(desiredModelsLoading || desiredStateProofLoading) && <CircularProgress size={16} />}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`desired_set: ${desiredModels?.desired_model_set_id ? String(desiredModels.desired_model_set_id).slice(0, 18) : 'none'}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`rev: ${typeof desiredModels?.desired_model_set_rev === 'number' ? desiredModels.desired_model_set_rev : 'n/a'}`}
                      />
                      {desiredModels?.updated_at && (
                        <Chip size="small" variant="outlined" label={`updated: ${String(desiredModels.updated_at).slice(0, 19)}Z`} />
                      )}
                      {typeof desiredStateProof?.domains?.models?.ok === 'boolean' && (
                        <Chip
                          size="small"
                          label={desiredStateProof.domains.models.ok ? 'applied: ok' : 'applied: FAIL'}
                          color={desiredStateProof.domains.models.ok ? 'success' : 'error'}
                          variant="outlined"
                        />
                      )}
                      {Array.isArray(desiredStateProof?.domains?.models?.missing_required) &&
                        desiredStateProof.domains.models.missing_required.length > 0 && (
                          <Chip
                            size="small"
                            label={`missing_required: ${desiredStateProof.domains.models.missing_required.length}`}
                            color="error"
                            variant="outlined"
                          />
                        )}
                      {typeof desiredStateProof?.staleness?.models?.desired_age_seconds === 'number' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`desired_age: ${Math.round(desiredStateProof.staleness.models.desired_age_seconds)}s`}
                        />
                      )}
                      {typeof desiredStateProof?.staleness?.models?.applied_age_seconds === 'number' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`applied_age: ${Math.round(desiredStateProof.staleness.models.applied_age_seconds)}s`}
                        />
                      )}
                    </Box>

                    {desiredModelsError && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        desired_models: {desiredModelsError}
                      </Alert>
                    )}

                    {/* Active models (from orchestrator pipeline logs) */}
                    {(() => {
                      const cams = Object.entries(edgePipelinesRuntime || {});
                      const rows = cams
                        .map(([cameraId, v]) => ({
                          cameraId,
                          pipeline: String((v as any)?.pipeline || ''),
                          models: (() => {
                            const norm = normalizeModelsInUse((v as any)?.models_in_use);
                            return norm.length ? norm : parseModelsFromPipelineLogTail((v as any)?.log_tail);
                          })(),
                        }))
                        .filter((r) => r.models.length > 0);
                      if (rows.length === 0) return null;
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Active models (used by pipeline)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Derived from the per-camera pipeline log tail (ground truth). This may differ from “Desired models” if extra models are present on disk.
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {rows.map((r) => (
                              <Box key={r.cameraId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                  {r.cameraId} {r.pipeline ? `· ${r.pipeline}` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {r.models.map((m) => (
                                    <Chip
                                      key={`${r.cameraId}-${m.role}-${m.modelPath}`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      label={`${m.role}: ${_basename(m.modelPath)}${m.labelsPath ? ` (labels: ${_basename(m.labelsPath)})` : ''}`}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 420px', minWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Desired models (cloud inventory)
                        </Typography>
                        {(() => {
                          // Compute which model *names* are actively referenced by running pipelines (per-camera).
                          const used = new Set<string>();
                          const cams = Object.entries(edgePipelinesRuntime || {});
                          for (const [, v] of cams) {
                            const norm = normalizeModelsInUse((v as any)?.models_in_use);
                            const ms = norm.length ? norm : parseModelsFromPipelineLogTail((v as any)?.log_tail);
                            for (const m of ms) {
                              const mn = _basename(m.modelPath);
                              if (mn) used.add(mn);
                              const ln = _basename(m.labelsPath);
                              if (ln) used.add(ln);
                            }
                          }
                          const anyUsed = used.size > 0;
                          return (
                            <>
                              {anyUsed ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  “unused” means not referenced by any running camera pipeline.
                                </Typography>
                              ) : null}
                              <List dense>
                                {(Array.isArray(desiredModels?.models) ? desiredModels.models : []).map((m: any) => {
                                  const name = String(m?.name || 'unknown');
                                  const isUsed = anyUsed ? used.has(name) : true;
                                  return (
                                    <ListItem key={`${String(m?.role || '')}:${String(m?.name || '')}:${String(m?.model_id || '')}`}>
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <span>{`${name}${m?.role ? ` (${String(m.role)})` : ''}`}</span>
                                            {!isUsed && (
                                              <Tooltip title="This file name is not referenced by any running camera pipeline model_path/labels_path.">
                                                <Chip size="small" label="unused" variant="outlined" color="warning" />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        }
                                        secondary={`model_id: ${String(m?.model_id || '').slice(0, 28)}${String(m?.model_id || '').length > 28 ? '…' : ''}${
                                          m?.required === false ? ' | optional' : ' | required'
                                        }`}
                                      />
                                    </ListItem>
                                  );
                                })}
                                {!Array.isArray(desiredModels?.models) || desiredModels.models.length === 0 ? (
                                  <ListItem>
                                    <ListItemText primary="No desired models configured." secondary="(This is allowed; the edge will treat models as satisfied.)" />
                                  </ListItem>
                                ) : null}
                              </List>
                            </>
                          );
                        })()}
                      </Box>

                      <Box sx={{ flex: '1 1 420px', minWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Models in use (edge)
                        </Typography>
                        {(() => {
                          // Prefer active pipeline models (ground truth) when available; fall back to applied (model_set_agent).
                          const activeModels: Array<{ role: string; modelPath: string; labelsPath?: string }> = [];
                          const cams = Object.entries(edgePipelinesRuntime || {});
                          for (const [, v] of cams) {
                            const norm = normalizeModelsInUse((v as any)?.models_in_use);
                            const ms = norm.length ? norm : parseModelsFromPipelineLogTail((v as any)?.log_tail);
                            for (const m of ms) {
                              if (m.modelPath && !activeModels.some((a) => a.modelPath === m.modelPath && a.role === m.role)) {
                                activeModels.push(m);
                              }
                            }
                          }
                          const appliedModels = Array.isArray(desiredStateProof?.domains?.models?.applied?.models)
                            ? desiredStateProof.domains.models.applied.models
                            : [];
                          const useActive = activeModels.length > 0;
                          const displayList = useActive ? activeModels : appliedModels;
                          return (
                            <List dense>
                              {useActive ? (
                                displayList.map((m: { role: string; modelPath: string; labelsPath?: string }, idx: number) => (
                                  <ListItem key={`active-${idx}-${m.role}-${_basename(m.modelPath)}`}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CheckCircle color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={`${m.role}: ${_basename(m.modelPath)}${m.labelsPath ? ` (+ ${_basename(m.labelsPath)})` : ''}`}
                                      secondary="From pipeline log (actively loaded)"
                                    />
                                  </ListItem>
                                ))
                              ) : (
                                displayList.map((m: any) => (
                                  <ListItem key={`${String(m?.name || '')}:${String(m?.model_id || '')}`}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      {m?.ok ? (
                                        <CheckCircle color="success" fontSize="small" />
                                      ) : (
                                        <ErrorIcon color="error" fontSize="small" />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={String(m?.name || 'unknown')}
                                      secondary={
                                        m?.ok
                                          ? `sha256:${String(m?.model_id || '').slice(7, 23)}… · verified`
                                          : `error: ${String(m?.error || 'unknown')}`
                                      }
                                    />
                                  </ListItem>
                                ))
                              )}
                              {displayList.length === 0 && (
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      cams.length > 0
                                        ? 'No models in use by any pipeline'
                                        : 'No models applied'
                                    }
                                    secondary={
                                      cams.length > 0
                                        ? 'Pipeline logs have no model entries yet.'
                                        : `Set ID: ${desiredStateProof?.domains?.models?.applied?.id || 'n/a'} · ${desiredStateProof?.domains?.models?.applied?.applied_at || ''}`
                                    }
                                  />
                                </ListItem>
                              )}
                              {!useActive &&
                                Array.isArray(desiredStateProof?.domains?.models?.reasons) &&
                                desiredStateProof.domains.models.reasons.length > 0 && (
                                  <ListItem>
                                    <ListItemText
                                      primary="reasons"
                                      secondary={desiredStateProof.domains.models.reasons.slice(0, 6).join(' | ')}
                                    />
                                  </ListItem>
                                )}
                            </List>
                          );
                        })()}
                      </Box>
                    </Box>

                    <Accordion sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">Details (raw JSON)</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: 12, overflowX: 'auto' }}>
                          {JSON.stringify(
                            {
                              pipeline_models: desiredStateProof?.domains?.models?.pipeline_models,
                              desired_models: desiredModels,
                              edge_models_domain: desiredStateProof?.domains?.models,
                              staleness_models: desiredStateProof?.staleness?.models,
                              restart: desiredStateProof?.restart,
                            },
                            null,
                            2
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleOpenSetDesiredModels}
                      disabled={desiredModelsLoading || setDesiredModelsSaving}
                    >
                      Set desired models
                    </Button>
                    <Button
                      size="small"
                      onClick={handleClearDesiredModels}
                      disabled={desiredModelsLoading || setDesiredModelsSaving || !Array.isArray(desiredModels?.models) || desiredModels.models.length === 0}
                      color="error"
                      startIcon={setDesiredModelsSaving ? <CircularProgress size={14} /> : <Delete />}
                    >
                      Clear desired
                    </Button>
                    <Button size="small" onClick={() => fetchDesiredModels()} disabled={desiredModelsLoading}>
                      Refresh desired
                    </Button>
                    <Button size="small" onClick={() => fetchDesiredStateProof()} disabled={desiredStateProofLoading}>
                      Refresh applied proof
                    </Button>
                  </CardActions>
                  </Collapse>
                </Card>
              </Grid>

              {/* Set desired models dialog */}
              <Dialog
                open={setDesiredModelsDialogOpen}
                onClose={() => !setDesiredModelsSaving && setSetDesiredModelsDialogOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>Set desired models</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick models from the registry. The edge will download and verify them. Use a name for config lookup (e.g. &quot;detector&quot;, &quot;classifier&quot;).
                  </Typography>
                  {modelsCatalogLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Registry ({modelsCatalog.length} models)
                      </Typography>
                      <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                        {modelsCatalog.map((m: any) => {
                          const mid = String(m?.model_id ?? '');
                          const isSelected = selectedModelsForDesired.some((s) => s.model_id === mid);
                          const name = String(m?.artifact_filename ?? m?.model_id ?? '');
                          return (
                            <ListItem
                              key={mid}
                              secondaryAction={
                                <Button
                                  size="small"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedModelsForDesired((prev) => prev.filter((s) => s.model_id !== mid));
                                    } else {
                                      setSelectedModelsForDesired((prev) => [
                                        ...prev,
                                        { model_id: mid, name: name.replace(/\.[^/.]+$/, '') || 'model', required: true },
                                      ]);
                                    }
                                  }}
                                >
                                  {isSelected ? 'Remove' : 'Add'}
                                </Button>
                              }
                            >
                              <ListItemText
                                primary={name}
                                secondary={`${mid.slice(0, 24)}… · ${m?.format ?? ''} · ${m?.size_bytes ? `${(m.size_bytes / 1024).toFixed(0)} KB` : ''}`}
                              />
                            </ListItem>
                          );
                        })}
                        {modelsCatalog.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No models in registry. Upload models first." />
                          </ListItem>
                        )}
                      </List>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected ({selectedModelsForDesired.length})
                      </Typography>
                      <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {selectedModelsForDesired.map((m, idx) => (
                          <ListItem
                            key={`${m.model_id}-${idx}`}
                            secondaryAction={
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setSelectedModelsForDesired((prev) => prev.filter((_, i) => i !== idx))
                                }
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            }
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                              <TextField
                                size="small"
                                label="Name"
                                value={m.name}
                                onChange={(e) =>
                                  setSelectedModelsForDesired((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    return next;
                                  })
                                }
                                sx={{ minWidth: 140 }}
                              />
                              <TextField
                                size="small"
                                label="Role (optional)"
                                placeholder="e.g. detector"
                                value={m.role ?? ''}
                                onChange={(e) =>
                                  setSelectedModelsForDesired((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], role: e.target.value || undefined };
                                    return next;
                                  })
                                }
                                sx={{ minWidth: 100 }}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={m.required}
                                    onChange={(e) =>
                                      setSelectedModelsForDesired((prev) => {
                                        const next = [...prev];
                                        next[idx] = { ...next[idx], required: e.target.checked };
                                        return next;
                                      })
                                    }
                                  />
                                }
                                label="Required"
                              />
                            </Box>
                          </ListItem>
                        ))}
                        {selectedModelsForDesired.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No models selected. Add from registry above." secondary="No desired models = edge treats models as satisfied." />
                          </ListItem>
                        )}
                      </List>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSetDesiredModelsDialogOpen(false)} disabled={setDesiredModelsSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveDesiredModels}
                    disabled={setDesiredModelsSaving}
                    startIcon={setDesiredModelsSaving ? <CircularProgress size={16} /> : <Save />}
                  >
                    {setDesiredModelsSaving ? 'Saving…' : 'Save'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Provisioner Runner */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Apply + Verify Runner" avatar={<DirectionsRun color="primary" />} />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Runs provisioning (optional) and a deterministic verification report.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip
                        size="small"
                        label={runnerLatest?.status || 'unknown'}
                        color={
                          runnerLatest?.status === 'succeeded'
                            ? 'success'
                            : runnerLatest?.status === 'failed'
                            ? 'error'
                            : runnerLatest?.status === 'running'
                            ? 'warning'
                            : 'default'
                        }
                      />
                      {runnerLatest?.run_id && <Chip size="small" variant="outlined" label={runnerLatest.run_id} />}
                      {runnerLatest?.started_at && (
                        <Chip size="small" variant="outlined" label={`started: ${runnerLatest.started_at}`} />
                      )}
                    </Box>
                    {runnerLatest?.error_code && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {runnerLatest.error_code}: {runnerLatest.error_detail || ''}
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={runnerUseLocalRepo}
                            onChange={(e) => setRunnerUseLocalRepo(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Deploy From Local Repo"
                      />
                      {runnerUseLocalRepo && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Local repo path override"
                          value={runnerRepoDir}
                          onChange={(e) => setRunnerRepoDir(e.target.value)}
                          helperText="Leave blank to use the API server's current edgeai checkout."
                        />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={fetchRunnerLatest} disabled={runnerLoading}>
                      Refresh
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      onClick={cancelProvisionerRun}
                      disabled={runnerLoading || !runnerLatest?.run_id || runnerLatest?.status !== 'running'}
                    >
                      Cancel
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => runProvisioner('verify_only')} disabled={runnerLoading}>
                      Run Verify Only
                    </Button>
                    <Button size="small" variant="contained" onClick={() => runProvisioner('apply_and_verify')} disabled={runnerLoading}>
                      Run Apply + Verify
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setRunnerDialogRunId(String(runnerLatest?.run_id || ''));
                        setRunnerDialogOpen(true);
                        if (runnerLatest?.run_id) {
                          fetchRunnerRun(String(runnerLatest.run_id));
                          fetchRunnerLogs(String(runnerLatest.run_id));
                        }
                      }}
                      disabled={!runnerLatest?.run_id}
                    >
                      View Report
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* ==================== DIAGNOSTICS TAB ==================== */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={3}>
              {/* System Resources */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="System Resources" avatar={<Memory color="primary" />} />
                  <CardContent>
                    {metricsLoaded ? (
                      <List>
                        <ListItem>
                          <ListItemText primary="CPU Usage" />
                          <Box sx={{ width: 200 }}>
                            <LinearProgress
                              variant="determinate"
                              value={metrics.cpu_percent}
                              color={metrics.cpu_percent > 80 ? 'error' : metrics.cpu_percent > 60 ? 'warning' : 'success'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'right' }}>
                              {metrics.cpu_percent.toFixed(0)}%
                            </Typography>
                          </Box>
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Memory Usage" />
                          <Box sx={{ width: 200 }}>
                            <LinearProgress
                              variant="determinate"
                              value={metrics.memory_percent}
                              color={
                                metrics.memory_percent > 80 ? 'error' : metrics.memory_percent > 60 ? 'warning' : 'success'
                              }
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'right' }}>
                              {metrics.memory_percent.toFixed(0)}%
                            </Typography>
                          </Box>
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Frame Rate" />
                          <Box sx={{ width: 200 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(metrics.fps / 35) * 100}
                              color={metrics.fps < 20 ? 'warning' : 'success'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'right' }}>
                              {metrics.fps.toFixed(1)} FPS
                            </Typography>
                          </Box>
                        </ListItem>
                      </List>
                    ) : (
                      <Box>
                        {[0, 1, 2].map((i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                            <Skeleton variant="text" width={100} />
                            <Box sx={{ width: 200 }}>
                              <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4 }} />
                              <Skeleton variant="text" width={40} sx={{ ml: 'auto', mt: 0.5 }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Pipeline Status" avatar={<CheckCircle color="success" />} />
                  <CardContent>
                    {pipelineStatus ? (
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <FiberManualRecord
                              sx={{
                                fontSize: 12,
                                color:
                                  pipelineStatus.state === 'running'
                                    ? 'success.main'
                                    : pipelineStatus.state === 'stopped'
                                    ? 'error.main'
                                    : 'warning.main',
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="State" secondary={pipelineStatus.state || 'Unknown'} />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Timeline fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Uptime"
                            secondary={pipelineStatus.uptime_seconds ? formatUptime(pipelineStatus.uptime_seconds) : '--'}
                          />
                        </ListItem>
                        {pipelineStatus.error && (
                          <ListItem>
                            <ListItemIcon>
                              <ErrorIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText primary="Error" secondary={pipelineStatus.error} />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <Box>
                        {[0, 1].map((i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                            <Skeleton variant="circular" width={12} height={12} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width={60} height={16} />
                              <Skeleton variant="text" width={100} height={18} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Incident E2E Health */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Incident → Evidence Health"
                    avatar={<Security color={incidentHealth?.ok ? 'success' : 'warning'} />}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          color={incidentHealth?.ok ? 'success' : 'warning'}
                          label={
                            incidentHealthLoading
                              ? 'Checking…'
                              : incidentHealth?.ok
                              ? 'OK'
                              : 'Degraded'
                          }
                        />
                        {incidentHealth?.timestamp && (
                          <Typography variant="caption" color="text.secondary">
                            {incidentHealth.timestamp}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <CardContent>
                    {incidentHealth?.error && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {incidentHealth.error}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Evidence directory
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary="Mount present"
                              secondary={
                                incidentHealth?.checks?.evidence_dir?.is_mountpoint ? 'Yes' : 'No'
                              }
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Writable"
                              secondary={incidentHealth?.checks?.evidence_dir?.writable ? 'Yes' : 'No'}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Latest evidence"
                              secondary={
                                incidentHealth?.checks?.evidence_dir?.latest_file?.name ||
                                'None yet'
                              }
                            />
                          </ListItem>
                          {incidentHealth?.checks?.evidence_dir?.latest_file && (
                            <ListItem>
                              <ListItemText
                                primary="Latest has JSON sidecar"
                                secondary={
                                  incidentHealth?.checks?.evidence_dir?.latest_file_has_sidecar
                                    ? 'Yes'
                                    : 'No'
                                }
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Docker services (supervisor)
                        </Typography>
                        <List dense>
                          {Object.entries(incidentHealth?.checks?.supervisor?.services || {}).map(
                            ([name, svc]: any) => (
                              <ListItem key={name}>
                                <ListItemText
                                  primary={name.replace('edge-services:', '')}
                                  secondary={svc?.state || 'unknown'}
                                />
                              </ListItem>
                            )
                          )}
                          {!Object.keys(incidentHealth?.checks?.supervisor?.services || {}).length && (
                            <ListItem>
                              <ListItemText primary="No supervisor data" secondary="Unavailable" />
                            </ListItem>
                          )}
                        </List>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Cloud API reachability (from edge)
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary="Cloud /health"
                              secondary={
                                incidentHealth?.checks?.cloud_api_health?.ok
                                  ? `OK (${incidentHealth?.checks?.cloud_api_health?.status})`
                                  : incidentHealth?.checks?.cloud_api_health
                                  ? `FAIL (${incidentHealth?.checks?.cloud_api_health?.status || 'n/a'})`
                                  : 'Not configured'
                              }
                            />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Software (collapsed) */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Software" avatar={<DirectionsRun color="secondary" />} />
                  <CardContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Update now (bundle), set desired release (OTA), or rollback — all without SSH.
                    </Typography>

                    <Accordion expanded={softwareBundleExpanded} onChange={(_, expanded) => setSoftwareBundleExpanded(expanded)}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                          <Typography variant="subtitle2">Update now (Bundle)</Typography>
                          {releaseLoading && <CircularProgress size={16} />}
                          <Chip size="small" label={releaseStatus?.status?.state || 'unknown'} />
                          {releaseStatus?.current?.release_id && (
                            <Chip size="small" variant="outlined" label={`current: ${releaseStatus.current.release_id}`} />
                          )}
                          {releaseStatus?.kgr?.release_id && (
                            <Chip size="small" variant="outlined" label={`kgr: ${releaseStatus.kgr.release_id}`} />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Stage, activate, and verify release bundles (A-56).
                        </Typography>
                        {releaseStatus?.status?.detail?.error && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            {releaseStatus.status.detail.error}
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Button size="small" onClick={fetchReleaseStatus} disabled={releaseLoading}>
                            Refresh
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setReleaseDialogOpen(true)}
                            disabled={runnerLoading || releaseLoading}
                          >
                            Update now…
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => runRunnerMode('verify_release')} disabled={runnerLoading}>
                            Verify
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={softwareOtaExpanded} onChange={(_, expanded) => setSoftwareOtaExpanded(expanded)}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                          <Typography variant="subtitle2">Set desired release (OTA)</Typography>
                          {(desiredReleaseLoading || agentStatusLoading || releasesCatalogLoading || otaPolling) && <CircularProgress size={16} />}
                          <Chip size="small" label={`desired: ${desiredReleaseInfo?.desired_release_id || 'none'}`} variant="outlined" />
                          <Chip
                            size="small"
                            label={`agent: ${String(agentStatus?.status || agentStatus?.error || 'unknown')}`}
                            color={String(agentStatus?.status || '').toLowerCase() === 'ok' ? 'success' : 'default'}
                            variant="outlined"
                          />
                          {desiredReleaseInfo?.desired_release?.image_digest_ref && (
                            <Chip size="small" label="digest set" color="info" variant="outlined" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Set a desired release in the cloud and let the edge pull+activate it.
                        </Typography>
                        <PlaybookCallout
                          title="Safe update / rollback sequence"
                          severity="warning"
                          compact
                          summary="Precheck before update. Use rollback only for failed release attempts, not config drift."
                          doItems={[
                            'Run precheck or Prepare for OTA before applying a desired release.',
                            'Use Rollback to known-good only after the latest release attempt is failing or support directs it.',
                          ]}
                          dontItems={[
                            'Do not switch desired release blindly while the edge is already APPLYING or STABILIZING.',
                            'Do not use rollback to fix config drift; use desired-state sync first.',
                          ]}
                          href={buildDocsUrl('training/EPIC12_WAREHOUSE_OPERATOR_TRAINING.md', 'rollback-vs-retry')}
                          linkLabel="Open operator playbook"
                          sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {otaPrecheckLoading && <CircularProgress size={16} />}
                          <Button size="small" variant="outlined" onClick={() => runOtaPrecheck()} disabled={otaPrecheckLoading || otaPolling}>
                            Run precheck
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => runOtaPrecheck({ autoFix: true })}
                            disabled={otaPrecheckLoading || otaPolling}
                          >
                            Auto-fix & recheck
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => runOtaPrecheck({ autoFix: true, pullTimeoutSec: 180 })}
                            disabled={otaPrecheckLoading || otaPolling}
                          >
                            Prepare for OTA
                          </Button>
                          {otaPrecheckResult && (
                            <Chip
                              size="small"
                              label={otaPrecheckResult?.ok ? 'precheck: PASS' : 'precheck: FAIL'}
                              color={otaPrecheckResult?.ok ? 'success' : 'error'}
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Desired release_id"
                          value={desiredReleaseId}
                          onChange={(e) => {
                            setDesiredReleaseId(e.target.value);
                            setDesiredReleaseDirty(true);
                          }}
                          helperText={
                            desiredReleaseInfo?.desired_release?.image_digest_ref
                              ? `image: ${String(desiredReleaseInfo.desired_release.image_digest_ref).slice(0, 90)}`
                              : 'Select a published release (or choose none to disable OTA).'
                          }
                          sx={{ mb: 1 }}
                        >
                          <MenuItem value="">
                            <em>(none)</em>
                          </MenuItem>
                          {desiredReleaseId &&
                            !releasesCatalog.some((r: any) => String(r.release_id) === String(desiredReleaseId)) && (
                              <MenuItem value={String(desiredReleaseId)}>{String(desiredReleaseId)} (selected)</MenuItem>
                            )}
                          {releasesCatalog.map((r: any) => (
                            <MenuItem key={String(r.release_id)} value={String(r.release_id)}>
                              {String(r.release_id)}
                            </MenuItem>
                          ))}
                        </TextField>

                        {agentStatus?.detail && (
                          <Alert severity={agentStatus?.ok === false ? 'warning' : 'info'} sx={{ mt: 1 }}>
                            {String(agentStatus.detail)}
                          </Alert>
                        )}

                        {(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state) && (
                          <Alert
                            sx={{ mt: 1 }}
                            severity={
                              String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || '')
                                .toUpperCase()
                                .includes('RUNNING')
                                ? 'success'
                                : String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || '')
                                    .toUpperCase()
                                    .includes('FAILED')
                                ? 'error'
                                : 'info'
                            }
                          >
                            Last attempt:{' '}
                            <strong>{String(releaseStatus?.last_attempt?.state || releaseStatus?.status?.state || 'unknown')}</strong>
                            {releaseStatus?.last_attempt?.attempt_id ? ` • attempt=${String(releaseStatus.last_attempt.attempt_id)}` : ''}
                            {releaseStatus?.last_attempt?.error ? ` — ${String(releaseStatus.last_attempt.error)}` : ''}
                          </Alert>
                        )}

                        {Array.isArray((releaseStatus as any)?.last_attempt?.warnings) && (releaseStatus as any).last_attempt.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            Release applied with warnings ({(releaseStatus as any).last_attempt.warnings.length}). See Diagnostics → Release journal for details.
                          </Alert>
                        )}

                        <Box sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={fetchReleaseAttempts} disabled={releaseAttemptsLoading}>
                            {releaseAttemptsLoading ? 'Loading attempts…' : 'Attempt history'}
                          </Button>
                        </Box>
                        {releaseAttemptsOpen && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Recent attempts (cloud)
                            </Typography>
                            {releaseAttempts.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No attempts recorded yet.
                              </Typography>
                            ) : (
                              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                                {releaseAttempts.slice(0, 8).map((a: any) => (
                                  <li key={String(a?.attempt_id || Math.random())}>
                                    <Typography variant="body2">
                                      <strong>{String(a?.attempt_id || 'attempt')}</strong>
                                      {a?.desired_release_id ? ` • desired=${String(a.desired_release_id)}` : ''}
                                      {a?.terminal_state ? ` • terminal=${String(a.terminal_state)}` : ''}
                                      {Array.isArray(a?.warnings) && a.warnings.length ? ` • warnings=${String(a.warnings.length)}` : ''}
                                      {a?.error_code ? ` • error=${String(a.error_code)}` : ''}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Button size="small" onClick={fetchDesiredRelease} disabled={desiredReleaseLoading}>
                            Refresh desired
                          </Button>
                          <Button size="small" onClick={fetchAgentStatus} disabled={agentStatusLoading}>
                            Refresh agent
                          </Button>
                          <Button size="small" onClick={fetchReleasesCatalog} disabled={releasesCatalogLoading}>
                            Refresh releases
                          </Button>
                          <Button size="small" variant="outlined" onClick={setDesiredRelease} disabled={desiredReleaseLoading}>
                            Save desired
                          </Button>
                          <Button size="small" variant="contained" onClick={triggerAgentPollNow} disabled={agentStatusLoading || otaPolling}>
                            Poll now
                          </Button>
                          <Button size="small" variant="contained" color="success" onClick={verifyOtaUpdate} disabled={otaVerifyLoading}>
                            {otaVerifyLoading ? 'Verifying…' : 'Verify update'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={runRollbackToKnownGood}
                            disabled={desiredReleaseLoading || agentStatusLoading || otaPolling}
                          >
                            Rollback to known-good
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={runCompleteUpdate}
                            disabled={desiredReleaseLoading || agentStatusLoading || otaPolling}
                          >
                            Apply desired release
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>

              {/* Provisioner Runner */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Apply + Verify Runner" avatar={<DirectionsRun color="primary" />} />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Runs provisioning (optional) and a deterministic verification report.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip
                        size="small"
                        label={runnerLatest?.status || 'unknown'}
                        color={
                          runnerLatest?.status === 'succeeded'
                            ? 'success'
                            : runnerLatest?.status === 'failed'
                            ? 'error'
                            : runnerLatest?.status === 'running'
                            ? 'warning'
                            : 'default'
                        }
                      />
                      {runnerLatest?.run_id && <Chip size="small" variant="outlined" label={runnerLatest.run_id} />}
                      {runnerLatest?.started_at && <Chip size="small" variant="outlined" label={`started: ${runnerLatest.started_at}`} />}
                    </Box>
                    {runnerLatest?.error_code && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {runnerLatest.error_code}: {runnerLatest.error_detail || ''}
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={runnerUseLocalRepo}
                            onChange={(e) => setRunnerUseLocalRepo(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Deploy From Local Repo"
                      />
                      {runnerUseLocalRepo && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Local repo path override"
                          value={runnerRepoDir}
                          onChange={(e) => setRunnerRepoDir(e.target.value)}
                          helperText="Leave blank to use the API server's current edgeai checkout."
                        />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={fetchRunnerLatest} disabled={runnerLoading}>
                      Refresh
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      onClick={cancelProvisionerRun}
                      disabled={runnerLoading || !runnerLatest?.run_id || runnerLatest?.status !== 'running'}
                    >
                      Cancel
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => runProvisioner('verify_only')} disabled={runnerLoading}>
                      Run Verify Only
                    </Button>
                    <Button size="small" variant="contained" onClick={() => runProvisioner('apply_and_verify')} disabled={runnerLoading}>
                      Run Apply + Verify
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setRunnerDialogRunId(String(runnerLatest?.run_id || ''));
                        setRunnerDialogOpen(true);
                        if (runnerLatest?.run_id) {
                          fetchRunnerRun(String(runnerLatest.run_id));
                          fetchRunnerLogs(String(runnerLatest.run_id));
                        }
                      }}
                      disabled={!runnerLatest?.run_id}
                    >
                      View Report
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Logs */}
              {/* Keep logs at the bottom so release/OTA controls stay visible above. */}
              <Grid item xs={12} sx={{ order: 999 }}>
                <Card>
                  <CardHeader
                    title="Device Logs"
                    avatar={<Terminal color="primary" />}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={logsEnabled}
                              onChange={(e) => setLogsEnabled(e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={logsEnabled ? 'primary' : 'text.secondary'}>
                              {logsEnabled ? 'Live' : 'Off'}
                            </Typography>
                          }
                        />
                        <Button
                          startIcon={logsLoading ? <CircularProgress size={16} /> : <Refresh />}
                          size="small"
                          onClick={fetchLogs}
                          disabled={logsLoading}
                        >
                          Refresh
                        </Button>
                      </Box>
                    }
                  />
                  <CardContent>
                    <Box
                      sx={{
                        bgcolor: 'grey.900',
                        color: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        height: 300,
                        overflow: 'auto',
                      }}
                    >
                      {logsLoading && logs.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : logs.length > 0 ? (
                        logs.map((log, i) => (
                          <Box
                            key={i}
                            component="pre"
                            sx={{
                              m: 0,
                              mb: 0.5,
                              whiteSpace: 'pre-wrap',
                              borderLeft: '3px solid',
                              borderColor: log.source === 'systemd' ? 'primary.main' : 'grey.600',
                              pl: 1,
                            }}
                          >
                            <Typography component="span" sx={{ color: 'grey.500', fontSize: 10 }}>
                              [{log.source}]
                            </Typography>{' '}
                            {log.message}
                          </Box>
                        ))
                      ) : (
                        <Typography color="grey.500">
                          No logs available. Click Refresh or enable Live streaming.
                        </Typography>
                      )}
                      <div ref={logsEndRef} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Provisioner runner report */}
      <Dialog open={runnerDialogOpen} onClose={() => setRunnerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Provisioner Run Report</DialogTitle>
        <DialogContent>
          {runnerLatest?.run_id ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, mt: 1 }}>
                <Chip size="small" variant="outlined" label={`run: ${runnerLatest.run_id}`} />
                <Chip size="small" color={runnerStatusColor(runnerLatest?.status) as any} label={runnerLatest?.status || 'unknown'} />
                {runnerLatest?.started_at && (
                  <Chip size="small" variant="outlined" label={`started: ${runnerLatest.started_at}`} />
                )}
                {runnerLatest?.finished_at && (
                  <Chip size="small" variant="outlined" label={`finished: ${runnerLatest.finished_at}`} />
                )}
              </Box>

              {(runnerLatest?.error_code || runnerSummaryStep) && (
                <Alert severity={runnerLatest?.status === 'failed' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                  <AlertTitle>
                    {runnerLatest?.status === 'failed' ? 'Run failed' : 'Run in progress'}
                  </AlertTitle>
                  {runnerLatest?.error_code && (
                    <Box sx={{ mb: runnerSummaryStep ? 1 : 0 }}>
                      <strong>{runnerLatest.error_code}</strong>
                      {runnerLatest?.error_detail ? `: ${runnerLatest.error_detail}` : ''}
                    </Box>
                  )}
                  {runnerSummaryStep && (
                    <Box>
                      Step <strong>{String(runnerSummaryStep.step_id || 'unknown')}</strong>
                      {runnerSummaryStep?.error_code ? ` • ${String(runnerSummaryStep.error_code)}` : ''}
                      {runnerSummaryStep?.error_detail ? ` • ${String(runnerSummaryStep.error_detail)}` : ''}
                    </Box>
                  )}
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Step details
              </Typography>
              {runnerSteps.length > 0 ? (
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {runnerSteps.map((step: any, idx: number) => (
                    <Box
                      key={`${String(step?.step_id || 'step')}-${idx}`}
                      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2">
                          {String(step?.step_id || `step_${idx + 1}`)}
                        </Typography>
                        <Chip size="small" color={runnerStatusColor(step?.status) as any} label={String(step?.status || 'unknown')} />
                        {step?.started_at && <Chip size="small" variant="outlined" label={`started: ${step.started_at}`} />}
                        {step?.finished_at && <Chip size="small" variant="outlined" label={`finished: ${step.finished_at}`} />}
                      </Box>
                      {step?.error_code && (
                        <Typography variant="body2" color="error.main" sx={{ mb: step?.error_detail ? 0.5 : 0 }}>
                          <strong>{String(step.error_code)}</strong>
                        </Typography>
                      )}
                      {step?.error_detail && (
                        <Typography variant="body2" sx={{ mb: step?.artifacts ? 1 : 0 }}>
                          {String(step.error_detail)}
                        </Typography>
                      )}
                      {step?.artifacts && (
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            fontSize: 12,
                            maxHeight: 160,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {JSON.stringify(step.artifacts, null, 2)}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No step details available yet. Refresh logs if the run is still in progress.
                </Alert>
              )}

              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Raw run payload</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontSize: 12,
                      maxHeight: 260,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {JSON.stringify(runnerLatest, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Typography variant="subtitle2" gutterBottom>
                Logs (tail)
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  borderRadius: 1,
                  fontSize: 12,
                  maxHeight: 260,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {(runnerLogs || []).map((l: any, idx: number) => (
                  <div key={idx}>
                    [{l.created_at}] {l.level || 'info'} {l.message}
                  </div>
                ))}
              </Box>
            </>
          ) : (
            <Typography>No run available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const targetRunId = runnerDialogRunId || runnerLatest?.run_id;
              if (!targetRunId) return;
              fetchRunnerRun(String(targetRunId));
              fetchRunnerLogs(String(targetRunId));
            }}
            disabled={!runnerDialogRunId && !runnerLatest?.run_id}
          >
            Refresh Logs
          </Button>
          <Button
            color="warning"
            onClick={() => cancelProvisionerRun()}
            disabled={runnerLoading || !runnerLatest?.run_id || runnerLatest?.status !== 'running'}
          >
            Cancel Run
          </Button>
          <Button
            onClick={() => {
              setRunnerDialogOpen(false);
              setRunnerDialogRunId('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release dialog */}
      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Activate Release Bundle</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Paste the release manifest JSON. Activation runs via the runner (activate_release).
          </Alert>
          <Button size="small" variant="outlined" onClick={generateBaselineManifest} disabled={releaseLoading || runnerLoading} sx={{ mb: 2 }}>
            Generate baseline manifest (from device)
          </Button>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Install bundle archive (optional)
          </Typography>
          <Button size="small" variant="outlined" onClick={buildReleaseBundle} disabled={releaseLoading || runnerLoading} sx={{ mb: 1 }}>
            Build + Install bundle on device (auto)
          </Button>
          <TextField
            label="Bundle path on device (e.g. /tmp/a56/bundle.tar.gz)"
            fullWidth
            value={releaseBundlePath}
            onChange={(e) => setReleaseBundlePath(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button size="small" variant="outlined" onClick={installReleaseBundle} disabled={releaseLoading || runnerLoading} sx={{ mb: 2 }}>
            Install bundle on device
          </Button>
          <TextField
            label="Release Manifest (JSON)"
            fullWidth
            multiline
            minRows={8}
            value={releaseManifestText}
            onChange={(e) => setReleaseManifestText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={releaseDryRun} onChange={(e) => setReleaseDryRun(e.target.checked)} />}
            label="Dry run (stage + validate only)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              try {
                const manifest = JSON.parse(releaseManifestText || '{}');
                if (!manifest || typeof manifest !== 'object' || !manifest.release_id || !manifest.runtime_mode || !(manifest.artifacts?.length > 0)) {
                  setSnackbar({ open: true, message: 'Manifest is empty/invalid. Click Build+Install (or Install) first.', severity: 'error' });
                  return;
                }
                runRunnerMode('activate_release', { release_manifest: manifest, dry_run: releaseDryRun });
                setReleaseDialogOpen(false);
              } catch (e) {
                setSnackbar({ open: true, message: 'Invalid JSON manifest', severity: 'error' });
              }
            }}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop confirmation dialog (with TTL) */}
      <Dialog open={stopDialog.open} onClose={() => setStopDialog((prev) => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Stop camera pipeline?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This is a manual override</AlertTitle>
            Stopping creates a runtime override on the edge so the orchestrator won’t auto-restart this camera until the override expires or you click Resume.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>
            Camera
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace' }}>
            {stopDialog.cameraId || '--'}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Duration
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={stopDialog.ttlSec === null ? 'indefinite' : String(stopDialog.ttlSec)}
            onChange={(e) => {
              const v = e.target.value;
              setStopDialog((prev) => ({ ...prev, ttlSec: v === 'indefinite' ? null : parseInt(v, 10) }));
            }}
            SelectProps={{ native: true }}
          >
            {stopTtlOptions.map((o) => (
              <option key={String(o.ttlSec)} value={o.ttlSec === null ? 'indefinite' : String(o.ttlSec)}>
                {o.label}
              </option>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {stopTtlOptions.find((o) => (stopDialog.ttlSec === null ? o.ttlSec === null : o.ttlSec === stopDialog.ttlSec))?.helper ||
              ' '}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialog((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmStopDialog}>
            Stop
          </Button>
        </DialogActions>
      </Dialog>

        </Grid>
        <Grid item xs={12} lg={4}>
          <SuggestedActionsRail context="device_control" deviceId={device?.device_id || pipelineDeviceId || undefined} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceControl;
